import { ErrorConstant, ExtensionPriority, __INTERNAL_REMIRROR_IDENTIFIER_KEY__, RemirrorIdentifier, ExtensionTag, ManagerPhase, EDITOR_CLASS_NAME } from '@remirror/core-constants';
export * from '@remirror/core-constants';
import { invariant, freeze, object, keys, camelCase, deepMerge, isEmptyArray, omit, sort, noop, isFunction, isPlainObject, isArray, isRemirrorType, isIdentifierOfType, pascalCase, bool, Cast, entries, uniqueBy, isPromise, uniqueArray, isNumber, isEmptyObject, isNullOrUndefined, isString, toString, includes, values, pick, uniqueId, getLazyArray } from '@remirror/core-helpers';
export * from '@remirror/core-helpers';
export * from '@remirror/core-types';
import { isMarkActive, convertCommand, findNodeAtPosition, getTextSelection, isNodeActive, isSelectionEmpty, mergeProsemirrorKeyBindings, isTextSelection, isNodeSelection, findChildren, getMarkRange, isElementDomNode, isProsemirrorNode, isProsemirrorMark, getDocument, toHtml, environment, createDocumentNode } from '@remirror/core-utils';
export * from '@remirror/core-utils';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _classPrivateFieldGet from '@babel/runtime/helpers/esm/classPrivateFieldGet';
import _classPrivateFieldSet from '@babel/runtime/helpers/esm/classPrivateFieldSet';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { TextSelection, PluginKey, Plugin, EditorState } from '@remirror/pm/state';
import { DecorationSet, Decoration } from '@remirror/pm/view';
import { toggleMark as toggleMark$1, baseKeymap, chainCommands, selectParentNode } from '@remirror/pm/commands';
import { inputRules, undoInputRule } from '@remirror/pm/inputrules';
import { keymap } from '@remirror/pm/keymap';
import { Schema } from '@remirror/pm/model';
import { addSuggester, removeSuggester, suggest, getSuggestPluginState } from '@remirror/pm/suggest';
import { cx } from 'linaria';
import { createNanoEvents } from 'nanoevents';

function defaultEquals(valueA, valueB) {
  return valueA === valueB;
}
/**
 * Get the property changes and the next value from an update.
 */


function getChangedOptions(parameter) {
  var previousOptions = parameter.previousOptions,
      update = parameter.update,
      _parameter$equals = parameter.equals,
      equals = _parameter$equals === void 0 ? defaultEquals : _parameter$equals;
  var next = freeze(_objectSpread(_objectSpread({}, previousOptions), update));
  var changes = object();
  var optionKeys = keys(previousOptions);

  var _iterator = _createForOfIteratorHelper(optionKeys),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var key = _step.value;
      var previousValue = previousOptions[key];
      var value = next[key];

      if (equals(previousValue, value)) {
        changes[key] = {
          changed: false
        };
        continue;
      }

      changes[key] = {
        changed: true,
        previousValue,
        value
      };
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  var pickChanged = keys => {
    var picked = object();

    var _iterator2 = _createForOfIteratorHelper(keys),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var key = _step2.value;
        var item = changes[key];

        if (item === null || item === void 0 ? void 0 : item.changed) {
          picked[key] = item.value;
        }
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }

    return picked;
  };

  return {
    changes: freeze(changes),
    options: next,
    pickChanged
  };
}
var codeLabelMap = {
  [ErrorConstant.DUPLICATE_HELPER_NAMES]: 'helper method',
  [ErrorConstant.DUPLICATE_COMMAND_NAMES]: 'command method'
};
/**
 * Checks whether a given string is unique to the set. Add the name if it
 * doesn't already exist, or throw an error when `shouldThrow` is true.
 *
 * @param parameter - destructured params
 */

function throwIfNameNotUnique(parameter) {
  var name = parameter.name,
      set = parameter.set,
      code = parameter.code;
  var label = codeLabelMap[code];
  !!set.has(name) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code,
    message: "There is a naming conflict for the name: ".concat(name, " used in this '").concat(label, "'. Please rename or remove from the editor to avoid runtime errors.")
  }) : invariant(false) : void 0;
  set.add(name);
}

var IGNORE = '__IGNORE__';
var GENERAL_OPTIONS = '__ALL__';

var _initialOptions = /*#__PURE__*/new WeakMap();

var _dynamicKeys = /*#__PURE__*/new WeakMap();

var _options = /*#__PURE__*/new WeakMap();

var _mappedHandlers = /*#__PURE__*/new WeakMap();

class BaseClass {
  /**
   * The default options for this extension.
   *
   * TODO see if this can be cast to something other than any and allow
   * composition.
   */

  /**
   * The static keys for this class.
   */

  /**
   * The event handler keys.
   */

  /**
   * Customize the way the handler should behave.
   */

  /**
   * The custom keys.
   */

  /**
   * Get the instance name of the instance from the constructor.
   *
   * - `'CorePreset'` => `'core'`
   * - `'AwesomeNodeExtension'` => `'awesomeNode'`
   *
   * The solution was adapted from https://stackoverflow.com/a/7888303/2172153.
   */
  static get instanceName() {
    // Make sure to camelCase the string (so that the first letter is
    // lowercase). `'BoldExtension'` => `'boldExtension'`
    return camelCase(this.name // Split by capitals `'boldExtension'` => `['bold', 'Extension']`.
    .split(/(?=[A-Z])/) // Drop the last index. `['bold', 'Extension']` => `['bold']`.
    .slice(0, -1) // Rejoin the word `['bold']` => `'bold'`.
    .join(''));
  }
  /**
   * This is not for external use. It is purely here for TypeScript inference of
   * the generic `Options` type parameter.
   */


  /**
   * The options for this extension.
   *
   * @remarks
   *
   * Options are composed of Static, Dynamic, Handlers and ObjectHandlers.
   *
   * - `Static` - set at instantiation by the constructor.
   * - `Dynamic` - optionally set at instantiation by the constructor and also
   *   set during the runtime.
   * - `Handlers` - can only be set during the runtime.
   * - `ObjectHandlers` - Can only be set during the runtime of the extension.
   */
  get options() {
    return _classPrivateFieldGet(this, _options);
  }
  /**
   * Get the dynamic keys for this extension.
   */


  get dynamicKeys() {
    return _classPrivateFieldGet(this, _dynamicKeys);
  }
  /**
   * The options that this instance was created with, merged with all the
   * default options.
   */


  get initialOptions() {
    return _classPrivateFieldGet(this, _initialOptions);
  }
  /**
   * The initial options at creation (used to reset).
   */


  constructor(_ref) {
    var validator = _ref.validator,
        defaultOptions = _ref.defaultOptions,
        code = _ref.code;

    _initialOptions.set(this, {
      writable: true,
      value: void 0
    });

    _dynamicKeys.set(this, {
      writable: true,
      value: void 0
    });

    _options.set(this, {
      writable: true,
      value: void 0
    });

    _mappedHandlers.set(this, {
      writable: true,
      value: void 0
    });

    validator(this.constructor, code);

    for (var _len = arguments.length, parameters = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      parameters[_key - 1] = arguments[_key];
    }

    var options = parameters[0];

    _classPrivateFieldSet(this, _mappedHandlers, object());

    this.populateMappedHandlers();

    _classPrivateFieldSet(this, _options, _classPrivateFieldSet(this, _initialOptions, deepMerge(defaultOptions, this.constructor.defaultOptions, options !== null && options !== void 0 ? options : object(), this.createDefaultHandlerOptions())));

    _classPrivateFieldSet(this, _dynamicKeys, this.getDynamicKeys()); // Triggers the `init` options update for this extension.


    this.init();
  }
  /**
   * This method is called by the extension constructor. It is not strictly a
   * lifecycle method since at this point the manager has not yet been
   * instantiated.
   *
   * @remarks
   *
   * It should be used instead of overriding the constructor which can lead to
   * problems.
   *
   * At this point
   * - `this.store` will throw an error since it doesn't yet exist.
   * - `this.type` in `NodeExtension` and `MarkExtension` will also throw an
   *   error since the schema hasn't been created yet.
   */


  init() {}
  /**
   * Clone the current instance with the provided options. If nothing is
   * provided it uses the same initial options as the current instance.
   */


  /**
   * Get the dynamic keys for this extension.
   */
  getDynamicKeys() {
    var dynamicKeys = [];
    var _this$constructor = this.constructor,
        customHandlerKeys = _this$constructor.customHandlerKeys,
        handlerKeys = _this$constructor.handlerKeys,
        staticKeys = _this$constructor.staticKeys;

    var _iterator = _createForOfIteratorHelper(keys(_classPrivateFieldGet(this, _options))),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _key2 = _step.value;

        if (staticKeys.includes(_key2) || handlerKeys.includes(_key2) || customHandlerKeys.includes(_key2)) {
          continue;
        }

        dynamicKeys.push(_key2);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    return dynamicKeys;
  }
  /**
   * Throw an error if non dynamic keys are updated.
   */


  ensureAllKeysAreDynamic(update) {
    if (process.env.NODE_ENV === 'production') {
      return;
    }

    var invalid = [];

    var _iterator2 = _createForOfIteratorHelper(keys(update)),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var _key3 = _step2.value;

        if (_classPrivateFieldGet(this, _dynamicKeys).includes(_key3)) {
          continue;
        }

        invalid.push(_key3);
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }

    !isEmptyArray(invalid) ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.INVALID_SET_EXTENSION_OPTIONS,
      message: "Invalid properties passed into the 'setOptions()' method: ".concat(JSON.stringify(invalid), ".")
    }) : invariant(false) : void 0;
  }
  /**
   * Update the properties with the provided partial value when changed.
   */


  setOptions(update) {
    var _this$onSetOptions;

    var previousOptions = this.getDynamicOptions();
    this.ensureAllKeysAreDynamic(update);

    var _getChangedOptions = getChangedOptions({
      previousOptions,
      update
    }),
        changes = _getChangedOptions.changes,
        options = _getChangedOptions.options,
        pickChanged = _getChangedOptions.pickChanged;

    this.updateDynamicOptions(options); // Trigger the update handler so the extension can respond to any relevant
    // property updates.

    (_this$onSetOptions = this.onSetOptions) === null || _this$onSetOptions === void 0 ? void 0 : _this$onSetOptions.call(this, {
      reason: 'set',
      changes,
      options,
      pickChanged,
      initialOptions: _classPrivateFieldGet(this, _initialOptions)
    });
  }
  /**
   * Reset the extension properties to their default values.
   *
   * @nonVirtual
   */


  resetOptions() {
    var _this$onSetOptions2;

    var previousOptions = this.getDynamicOptions();

    var _getChangedOptions2 = getChangedOptions({
      previousOptions,
      update: _classPrivateFieldGet(this, _initialOptions)
    }),
        changes = _getChangedOptions2.changes,
        options = _getChangedOptions2.options,
        pickChanged = _getChangedOptions2.pickChanged;

    this.updateDynamicOptions(options); // Trigger the update handler so that child extension properties can also be
    // updated.

    (_this$onSetOptions2 = this.onSetOptions) === null || _this$onSetOptions2 === void 0 ? void 0 : _this$onSetOptions2.call(this, {
      reason: 'reset',
      options,
      changes,
      pickChanged,
      initialOptions: _classPrivateFieldGet(this, _initialOptions)
    });
  }
  /**
   * Override this to receive updates whenever the options have been updated on
   * this instance. This method is called after the updates have already been
   * applied to the instance. If you need more control over exactly how the
   * option should be applied you should set the option to be `Custom`.
   *
   * **Please Note**:
   *
   * This must be defined as a instance method and not a property since it is
   * called in the constructor.
   *
   * ```ts
   * class ThisPreset extends Preset {
   *   // GOOD ✅
   *   onSetOptions(parameter: OnSetOptionsParameter<Options>) {}
   *
   *    // BAD ❌
   *   onSetOptions = (parameter: OnSetOptionsParameter<Options>) => {}
   * }
   * ```
   *
   * @abstract
   */


  /**
   * Update the private options.
   */
  getDynamicOptions() {
    return omit(_classPrivateFieldGet(this, _options), [...this.constructor.customHandlerKeys, ...this.constructor.handlerKeys]);
  }
  /**
   * Update the dynamic options.
   */


  updateDynamicOptions(options) {
    _classPrivateFieldSet(this, _options, _objectSpread(_objectSpread({}, _classPrivateFieldGet(this, _options)), options));
  }
  /**
   * Set up the mapped handlers object with default values (an empty array);
   */


  populateMappedHandlers() {
    var _iterator3 = _createForOfIteratorHelper(this.constructor.handlerKeys),
        _step3;

    try {
      for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
        var _key4 = _step3.value;
        _classPrivateFieldGet(this, _mappedHandlers)[_key4] = [];
      }
    } catch (err) {
      _iterator3.e(err);
    } finally {
      _iterator3.f();
    }
  }
  /**
   * This is currently fudged together, I'm not sure it will work.
   */


  createDefaultHandlerOptions() {
    var _this = this;

    var methods = object();

    var _iterator4 = _createForOfIteratorHelper(this.constructor.handlerKeys),
        _step4;

    try {
      var _loop = function _loop() {
        var key = _step4.value;

        methods[key] = function () {
          var returnValue;

          var _iterator5 = _createForOfIteratorHelper(_classPrivateFieldGet(_this, _mappedHandlers)[key]),
              _step5;

          try {
            for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
              var _step5$value = _slicedToArray(_step5.value, 2),
                  handler = _step5$value[1];

              returnValue = handler(...arguments);
              var handlerKeyOptions = _this.constructor.handlerKeyOptions; // Check if the method should cause an early return, based on the
              // return value.

              if (shouldReturnEarly(handlerKeyOptions, returnValue, key)) {
                return returnValue;
              }
            }
          } catch (err) {
            _iterator5.e(err);
          } finally {
            _iterator5.f();
          }

          return returnValue;
        };
      };

      for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
        _loop();
      }
    } catch (err) {
      _iterator4.e(err);
    } finally {
      _iterator4.f();
    }

    return methods;
  }
  /**
   * Add a handler to the event handlers so that it is called along with all the
   * other handler methods.
   *
   * This is helpful for integrating react hooks which can be used in multiple
   * places. The original problem with fixed properties is that you can only
   * assign to a method once and it overwrites any other methods. This pattern
   * for adding handlers allows for multiple usages of the same handler in the
   * most relevant part of the code.
   *
   * More to come on this pattern.
   *
   * @nonVirtual
   */


  addHandler(key, method) {
    var priority = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ExtensionPriority.Default;

    _classPrivateFieldGet(this, _mappedHandlers)[key].push([priority, method]);

    this.sortHandlers(key); // Return a method for disposing of the handler.

    return () => _classPrivateFieldGet(this, _mappedHandlers)[key] = _classPrivateFieldGet(this, _mappedHandlers)[key].filter((_ref2) => {
      var _ref3 = _slicedToArray(_ref2, 2),
          handler = _ref3[1];

      return handler !== method;
    });
  }

  sortHandlers(key) {
    _classPrivateFieldGet(this, _mappedHandlers)[key] = sort(_classPrivateFieldGet(this, _mappedHandlers)[key], // Sort from highest binding to the lowest.
    (_ref4, _ref5) => {
      var _ref6 = _slicedToArray(_ref4, 1),
          a = _ref6[0];

      var _ref7 = _slicedToArray(_ref5, 1),
          z = _ref7[0];

      return z - a;
    });
  }
  /**
   * A method that can be used to add a custom handler. It is up to the
   * extension creator to manage the handlers and dispose methods.
   */


  addCustomHandler(key, value) {
    var _this$onAddCustomHand, _this$onAddCustomHand2;

    return (_this$onAddCustomHand = (_this$onAddCustomHand2 = this.onAddCustomHandler) === null || _this$onAddCustomHand2 === void 0 ? void 0 : _this$onAddCustomHand2.call(this, {
      [key]: value
    })) !== null && _this$onAddCustomHand !== void 0 ? _this$onAddCustomHand : noop;
  }
  /**
   * Override this method if you want to set custom handlers on your extension.
   *
   * This must return a dispose function.
   */


}

_defineProperty(BaseClass, "defaultOptions", {});

_defineProperty(BaseClass, "staticKeys", []);

_defineProperty(BaseClass, "handlerKeys", []);

_defineProperty(BaseClass, "handlerKeyOptions", {});

_defineProperty(BaseClass, "customHandlerKeys", []);

/**
 * A function used to determine whether the value provided by the handler
 * warrants an early return.
 */
function shouldReturnEarly(handlerKeyOptions, returnValue, handlerKey) {
  var generalOptions = handlerKeyOptions[GENERAL_OPTIONS];
  var handlerOptions = handlerKeyOptions[handlerKey];

  if (!generalOptions && !handlerOptions) {
    return false;
  } // First check if there are options set for the provided handlerKey


  if (handlerOptions && // Only proceed if the value should not be ignored.
  handlerOptions.earlyReturnValue !== IGNORE && (isFunction(handlerOptions.earlyReturnValue) ? handlerOptions.earlyReturnValue(returnValue) === true : returnValue === handlerOptions.earlyReturnValue)) {
    return true;
  }

  if (generalOptions && // Only proceed if they are not ignored.
  generalOptions.earlyReturnValue !== IGNORE && ( // Check whether the `earlyReturnValue` is a predicate check.
  isFunction(generalOptions.earlyReturnValue) ? // If it is a predicate and when called with the current
  // `returnValue` the value is `true` then we should return
  // early.
  generalOptions.earlyReturnValue(returnValue) === true : // Check the actual return value.
  returnValue === generalOptions.earlyReturnValue)) {
    return true;
  }

  return false;
}
/**
 * @internal
 */


/**
 * Checks that the extension has a valid constructor with the `defaultOptions`
 * and `defaultProperties` defined as static properties.
 */
function isValidConstructor(Constructor, code) {
  !isPlainObject(Constructor.defaultOptions) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    message: "No static 'defaultOptions' provided for '".concat(Constructor.name, "'.\n"),
    code
  }) : invariant(false) : void 0;
  !isArray(Constructor.staticKeys) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    message: "No static 'staticKeys' provided for '".concat(Constructor.name, "'.\n"),
    code
  }) : invariant(false) : void 0;
  !isArray(Constructor.handlerKeys) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    message: "No static 'handlerKeys' provided for '".concat(Constructor.name, "'.\n"),
    code
  }) : invariant(false) : void 0;
  !isArray(Constructor.customHandlerKeys) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    message: "No static 'customHandlerKeys' provided for '".concat(Constructor.name, "'.\n"),
    code
  }) : invariant(false) : void 0;
}

/**
 * Auto infers the parameter for the constructor. If there is a required static
 * option then the TypeScript compiler will error if nothing is passed in.
 */

var _priorityOverride = /*#__PURE__*/new WeakMap();

var _store = /*#__PURE__*/new WeakMap();

/**
 * Extensions are fundamental to the way that Remirror works by grouping
 * together the functionality and handling the management of similar concerns.
 *
 * @remarks
 *
 *  Extension can adjust editor functionality in any way. Here are some
 *  examples.
 *
 * - How the editor displays certain content, i.e. **bold**, _italic_,
 *   **underline**.
 * - Which commands should be made available e.g. `commands.toggleBold()` to
 *   toggle the weight of the selected text.
 * - Check if a command is currently enabled (i.e a successful dry run) e.g.
 *   `commands.toggleBold.isEnabled()`.
 * - Register Prosemirror `Plugin`s, `keymap`s, `InputRule`s `PasteRule`s,
 *   `Suggestions`, and custom `nodeViews` which affect the behavior of the
 *   editor.
 *
 * There are three types of `Extension`.
 *
 * - `NodeExtension` - For creating Prosemirror nodes in the editor. See
 *   {@link NodeExtension}
 * - `MarkExtension` - For creating Prosemirror marks in the editor. See
 *   {@link MarkExtension}
 * - `PlainExtension` - For behavior which doesn't map to a `ProsemirrorNode` or
 *   `Mark` and as a result doesn't directly affect the Prosemirror `Schema` or
 *   content. See {@link PlainExtension}.
 *
 * This `Extension` is an abstract class that should not be used directly but
 * rather extended to add the intended functionality.
 *
 * ```ts
 * import { PlainExtension, Static } from 'remirror/core';
 *
 * interface AwesomeExtensionOptions {
 *   isAwesome?: Static<boolean>;
 *   id?: string;
 * }
 *
 * class AwesomeExtension extends PlainExtension<AwesomeExtensionOptions> {
 *   static defaultOptions: DefaultExtensionOptions<AwesomeExtensionOptions> = {
 *     isAwesome: true,
 *     id: '',
 *   }
 *
 *   get name() {
 *     return 'awesome' as const;
 *   }
 * }
 * ```
 */
class Extension extends BaseClass {
  /**
   * The default priority for this family of extensions.
   */

  /**
   * Allows for the `RemirrorManager` or `Preset`'s to override the priority of
   * an extension.
   */

  /**
   * The priority level for this instance of the extension. A higher value
   * corresponds to a higher priority extension
   */
  get priority() {
    var _ref, _classPrivateFieldGet2;

    return (_ref = (_classPrivateFieldGet2 = _classPrivateFieldGet(this, _priorityOverride)) !== null && _classPrivateFieldGet2 !== void 0 ? _classPrivateFieldGet2 : this.options.priority) !== null && _ref !== void 0 ? _ref : this.constructor.defaultPriority;
  }
  /**
   * The name that the constructor should have, which doesn't get mangled in
   * production.
   */


  get constructorName() {
    return "".concat(pascalCase(this.name), "Extension");
  }
  /**
   * The store is a property that's internal to extension. It include important
   * items like the `view` and `schema` that are added by the extension manager
   * and also the lifecycle extension methods.
   *
   * **NOTE** - The store is not available until the manager has been created
   * and received the extension. As a result trying to access the store during
   * `init` and `constructor` will result in a runtime error.
   *
   * Some properties of the store are available at different phases. You should
   * check the inline documentation to know when a certain property is useable
   * in your extension.
   */


  get store() {
    !_classPrivateFieldGet(this, _store) ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.MANAGER_PHASE_ERROR,
      message: "An error occurred while attempting to access the 'extension.store' when the Manager has not yet set created the lifecycle methods."
    }) : invariant(false) : void 0;
    return freeze(_classPrivateFieldGet(this, _store), {
      requireKeys: true
    });
  }
  /**
   * This store is can be modified by the extension manager with and lifecycle
   * extension methods.
   *
   * Different properties are added at different times so it's important to
   * check the documentation for each property to know what phase is being
   * added.
   */


  constructor() {
    for (var _len = arguments.length, parameters = new Array(_len), _key = 0; _key < _len; _key++) {
      parameters[_key] = arguments[_key];
    }

    super({
      validator: isValidExtensionConstructor,
      defaultOptions,
      code: ErrorConstant.INVALID_EXTENSION
    }, ...parameters);

    _priorityOverride.set(this, {
      writable: true,
      value: void 0
    });

    _store.set(this, {
      writable: true,
      value: void 0
    });
  }
  /**
   * Check if the type of this extension's constructor matches the type of the
   * provided constructor.
   */


  isOfType(Constructor) {
    return this.constructor === Constructor;
  }
  /**
   * Pass a reference to the globally shared `ExtensionStore` for this
   * extension.
   *
   * @remarks
   *
   * The extension store allows extensions to access important variables without
   * complicating their creator methods.
   *
   * ```ts
   * import { PlainExtension } from 'remirror/core';
   *
   * class Awesome extends PlainExtension {
   *   customMethod() {
   *     if (this.store.view.hasFocus()) {
   *       log('dance dance dance');
   *     }
   *   }
   * }
   * ```
   *
   * This should only be called by the `RemirrorManager`.
   *
   * @internal
   * @nonVirtual
   */


  setStore(store) {
    if (_classPrivateFieldGet(this, _store)) {
      return;
    }

    _classPrivateFieldSet(this, _store, store);
  }
  /**
   * Clone an extension.
   */


  clone() {
    for (var _len2 = arguments.length, parameters = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      parameters[_key2] = arguments[_key2];
    }

    return new this.constructor(...parameters);
  }
  /**
   * Set the priority override for this extension. This is used in the
   * `RemirrorManager` in order to override the priority of an extension.
   *
   * If you set the first parameter to `undefined` it will remove the priority
   * override.
   */


  setPriority(priority) {
    _classPrivateFieldSet(this, _priorityOverride, priority);
  }

}
/**
 * Declaration merging since the constructor property can't be defined on the
 * actual class.
 */


_defineProperty(Extension, "defaultPriority", ExtensionPriority.Default);

/**
 * Create a plain extension which doesn't directly map to Prosemirror nodes or
 * marks.
 *
 * Plain extensions are a great way to add custom behavior to your editor.
 */
class PlainExtension extends Extension {
  static get [__INTERNAL_REMIRROR_IDENTIFIER_KEY__]() {
    return RemirrorIdentifier.PlainExtensionConstructor;
  }

  get [__INTERNAL_REMIRROR_IDENTIFIER_KEY__]() {
    return RemirrorIdentifier.PlainExtension;
  }

}
/**
 * A mark extension is based on the `Mark` concept from from within prosemirror
 * {@link https://prosemirror.net/docs/guide/#schema.marks}
 *
 * @remarks
 *
 * Marks are used to add extra styling or other information to inline content.
 * Mark types are objects much like node types, used to tag mark objects and
 * provide additional information about them.
 */

class MarkExtension extends Extension {
  static get [__INTERNAL_REMIRROR_IDENTIFIER_KEY__]() {
    return RemirrorIdentifier.MarkExtensionConstructor;
  }
  /**
   * Whether to disable extra attributes for this extension.
   */


  get [__INTERNAL_REMIRROR_IDENTIFIER_KEY__]() {
    return RemirrorIdentifier.MarkExtension;
  }
  /**
   * Provides access to the mark type from the schema.
   *
   * @remarks
   *
   * The type is available when the manager initializes. So it can be used in
   * the outer scope of `createCommands`, `createHelpers`, `createKeymap` and
   * most of the creator methods.
   */


  get type() {
    return this.store.schema.marks[this.name];
  }

  constructor() {
    super(...arguments);
  }
  /**
   * Provide a method for creating the schema. This is required in order to
   * create a `MarkExtension`.
   *
   * @remarks
   *
   * The main difference between the return value of this method and Prosemirror
   * `MarkSpec` is that that the `toDOM` method doesn't allow dom manipulation.
   * You can only return an array or string.
   *
   * For more advanced requirements, it may be possible to create a `nodeView`
   * to manage the dom interactions.
   */


}

_defineProperty(MarkExtension, "disableExtraAttributes", false);

/**
 * Defines the abstract class for extensions which can place nodes into the
 * prosemirror state.
 *
 * @remarks
 *
 * For more information see {@link https://prosemirror.net/docs/ref/#model.Node}
 */
class NodeExtension extends Extension {
  static get [__INTERNAL_REMIRROR_IDENTIFIER_KEY__]() {
    return RemirrorIdentifier.NodeExtensionConstructor;
  }
  /**
   * Whether to disable extra attributes for this extension.
   */


  get [__INTERNAL_REMIRROR_IDENTIFIER_KEY__]() {
    return RemirrorIdentifier.NodeExtension;
  }
  /**
   * Provides access to the node type from the schema.
   */


  get type() {
    return this.store.schema.nodes[this.name];
  }

  constructor() {
    super(...arguments);
  }
  /**
   * Provide a method for creating the schema. This is required in order to
   * create a `NodeExtension`.
   *
   * @remarks
   *
   * A node schema defines the behavior of the content within the editor. This
   * is very tied to the prosemirror implementation and the best place to learn
   * more about it is in the
   * {@link https://prosemirror.net/docs/guide/#schema docs}.
   *
   * @params hole - a method that is meant to indicate where extra attributes
   * should be placed (if they exist).
   *
   * The `hole` is a function that augments the passed object adding a special
   * `secret` key which is used to insert the extra attributes setter.
   *
   * ```ts
   * import { NodeExtension, SpecHole } from 'remirror/core';
   *
   * class AwesomeExtension extends NodeExtension {
   *   get name() { return 'awesome' as const'; }
   *
   *   createNodeSpec() {
   *     return {
   *       toDOM: (node) => {
   *         return ['p', hole(), 0]
   *       }
   *     }
   *   }
   * }
   * ```
   *
   * The above example will have the `hole()` method call replaced with the
   * extra attributes.
   */


}

_defineProperty(NodeExtension, "disableExtraAttributes", false);

/**
 * These are the default options merged into every extension. They can be
 * overridden.
 */
var defaultOptions = {
  priority: undefined,
  extraAttributes: {},
  disableExtraAttributes: false,
  exclude: {}
};
/**
 * Mutate the default extension options.
 *
 * @remarks
 *
 * This is a dangerous method since it allows you to mutate the received object.
 * Don't use it unless you absolutely have to.
 *
 * A potential use case is for adding a new default option to all extensions. It
 * shows an example of how to accomplish this in a typesafe way.
 *
 * ```ts
 * import { mutateDefaultExtensionOptions } from 'remirror/core';
 *
 * mutateDefaultExtensionOptions((settings) => {
 *   // Set the default value of all extensions to have a property `customSetting` with value `false`.
 *   settings.customSetting = false;
 * })
 *
 * declare global {
 *   namespace Remirror {
 *     interface BaseExtensionOptions {
 *       customSetting?: boolean;
 *     }
 *   }
 * }
 *```
 *
 * The mutation must happen before any extension have been instantiated.
 */

function mutateDefaultExtensionOptions(mutatorMethod) {
  mutatorMethod(defaultOptions);
}
/**
 * Determines if the passed value is an extension.
 *
 * @param value - the value to test
 */

function isExtension(value) {
  return isRemirrorType(value) && isIdentifierOfType(value, [RemirrorIdentifier.PlainExtension, RemirrorIdentifier.MarkExtension, RemirrorIdentifier.NodeExtension]);
}
/**
 * Checks that the extension has a valid constructor with the `defaultOptions`
 * and `defaultProperties` defined as static properties.
 */

function isValidExtensionConstructor(Constructor) {
  var code = ErrorConstant.INVALID_EXTENSION;
  !isExtensionConstructor(Constructor) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    message: 'This is not a valid extension constructor',
    code
  }) : invariant(false) : void 0;
  isValidConstructor(Constructor, code);
}
/**
 * Determines if the passed value is an extension constructor.
 *
 * @param value - the value to test
 */

function isExtensionConstructor(value) {
  return isRemirrorType(value) && isIdentifierOfType(value, [RemirrorIdentifier.PlainExtensionConstructor, RemirrorIdentifier.MarkExtensionConstructor, RemirrorIdentifier.NodeExtensionConstructor]);
}
/**
 * Checks whether the provided value is a plain extension.
 *
 * @param value - the extension to check
 */

function isPlainExtension(value) {
  return isRemirrorType(value) && isIdentifierOfType(value, RemirrorIdentifier.PlainExtension);
}
/**
 * Determines if the passed in extension is a node extension. Useful as a type
 * guard where a particular type of extension is needed.
 *
 * @param value - the extension to check
 */

function isNodeExtension(value) {
  return isRemirrorType(value) && isIdentifierOfType(value, RemirrorIdentifier.NodeExtension);
}
/**
 * Determines if the passed in extension is a mark extension. Useful as a type
 * guard where a particular type of extension is needed.
 *
 * @param value - the extension to check
 */

function isMarkExtension(value) {
  return isRemirrorType(value) && isIdentifierOfType(value, RemirrorIdentifier.MarkExtension);
}

var _attributeList = /*#__PURE__*/new WeakMap();

var _attributeObject = /*#__PURE__*/new WeakMap();

/**
 * This extension allows others extension to add the `createAttributes` method
 * for adding attributes to the prosemirror dom element.
 *
 * @remarks
 *
 * Use this to include all the dynamically generated attributes provided by each
 * extension. High priority extensions have preference over the lower priority
 * extensions.
 *
 * @builtin
 */
class AttributesExtension extends PlainExtension {
  constructor() {
    var _this;

    super(...arguments);
    _this = this;

    _attributeList.set(this, {
      writable: true,
      value: []
    });

    _attributeObject.set(this, {
      writable: true,
      value: object()
    });

    _defineProperty(this, "updateAttributes", function () {
      var triggerUpdate = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;

      _this.transformAttributes();

      if (triggerUpdate) {
        _this.store.commands.forceUpdate('attributes');
      }
    });
  }

  get name() {
    return 'attributes';
  }

  /**
   * Create the attributes object on initialization.
   *
   * @internal
   */
  onCreate() {
    this.transformAttributes();
    this.store.setExtensionStore('updateAttributes', this.updateAttributes);
  }

  transformAttributes() {
    // Reset this attributes
    _classPrivateFieldSet(this, _attributeList, []);

    _classPrivateFieldSet(this, _attributeObject, object());

    var _iterator = _createForOfIteratorHelper(this.store.extensions),
        _step;

    try {
      extensionLoop: for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _this$store$managerSe, _extension$options$ex;

        var extension = _step.value;

        if (!extension.createAttributes || ((_this$store$managerSe = this.store.managerSettings.exclude) === null || _this$store$managerSe === void 0 ? void 0 : _this$store$managerSe.attributes) || ((_extension$options$ex = extension.options.exclude) === null || _extension$options$ex === void 0 ? void 0 : _extension$options$ex.attributes)) {
          continue extensionLoop;
        } // Inserted at the start of the list so that when combining the full
        // attribute object the higher priority extension attributes are
        // preferred to the lower priority since they merge with the object
        // later.


        _classPrivateFieldGet(this, _attributeList).unshift(extension.createAttributes());
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    var _iterator2 = _createForOfIteratorHelper(_classPrivateFieldGet(this, _attributeList)),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var _classPrivateFieldGet2;

        var attributes = _step2.value;

        _classPrivateFieldSet(this, _attributeObject, _objectSpread(_objectSpread(_objectSpread({}, _classPrivateFieldGet(this, _attributeObject)), attributes), {}, {
          class: ((_classPrivateFieldGet2 = _classPrivateFieldGet(this, _attributeObject).class) !== null && _classPrivateFieldGet2 !== void 0 ? _classPrivateFieldGet2 : '') + (bool(attributes.class) ? attributes.class : '') || ''
        }));
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }

    this.store.setStoreKey('attributes', _classPrivateFieldGet(this, _attributeObject));
  }

}

/**
 * A decorator for the remirror extension.
 *
 * This adds static properties to the extension constructor.
 */
function extensionDecorator(options) {
  return ReadonlyConstructor => {
    var defaultOptions = options.defaultOptions,
        customHandlerKeys = options.customHandlerKeys,
        handlerKeys = options.handlerKeys,
        staticKeys = options.staticKeys,
        defaultPriority = options.defaultPriority,
        handlerKeyOptions = options.handlerKeyOptions,
        rest = _objectWithoutProperties(options, ["defaultOptions", "customHandlerKeys", "handlerKeys", "staticKeys", "defaultPriority", "handlerKeyOptions"]);

    var Constructor = Cast(ReadonlyConstructor);

    if (defaultOptions) {
      Constructor.defaultOptions = defaultOptions;
    }

    if (defaultPriority) {
      Constructor.defaultPriority = defaultPriority;
    }

    if (handlerKeyOptions) {
      Constructor.handlerKeyOptions = handlerKeyOptions;
    }

    Constructor.staticKeys = staticKeys !== null && staticKeys !== void 0 ? staticKeys : [];
    Constructor.handlerKeys = handlerKeys !== null && handlerKeys !== void 0 ? handlerKeys : [];
    Constructor.customHandlerKeys = customHandlerKeys !== null && customHandlerKeys !== void 0 ? customHandlerKeys : [];

    var _iterator = _createForOfIteratorHelper(entries(rest)),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _step$value = _slicedToArray(_step.value, 2),
            key = _step$value[0],
            value = _step$value[1];

        if (Constructor[key]) {
          continue;
        }

        Constructor[key] = value;
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    return Cast(Constructor);
  };
}

/**
 * A decorator for the remirror preset.
 *
 * This adds static properties to the preset constructor.
 */
function presetDecorator(options) {
  return ReadonlyConstructor => {
    var defaultOptions = options.defaultOptions,
        customHandlerKeys = options.customHandlerKeys,
        handlerKeys = options.handlerKeys,
        staticKeys = options.staticKeys;
    var Constructor = Cast(ReadonlyConstructor);

    if (defaultOptions) {
      Constructor.defaultOptions = defaultOptions;
    }

    Constructor.staticKeys = staticKeys !== null && staticKeys !== void 0 ? staticKeys : [];
    Constructor.handlerKeys = handlerKeys !== null && handlerKeys !== void 0 ? handlerKeys : [];
    Constructor.customHandlerKeys = customHandlerKeys !== null && customHandlerKeys !== void 0 ? customHandlerKeys : [];
    return Cast(Constructor);
  };
}

var _extensionStore = /*#__PURE__*/new WeakMap();

var _extensions = /*#__PURE__*/new WeakMap();

var _extensionMap = /*#__PURE__*/new WeakMap();

/**
 * A `Preset` is one way of bundling together similar extensions with unified
 * options and dynamic properties.
 *
 * It can also set new options which dynamically update multiple options for
 * different extensions.
 */
class Preset extends BaseClass {
  /**
   * The default options for this preset.
   */

  /**
   * The preset constructor identifier key.
   *
   * @internal
   */
  static get [__INTERNAL_REMIRROR_IDENTIFIER_KEY__]() {
    return RemirrorIdentifier.PresetConstructor;
  }
  /**
   * The remirror identifier key.
   *
   * @internal
   */


  get [__INTERNAL_REMIRROR_IDENTIFIER_KEY__]() {
    return RemirrorIdentifier.Preset;
  }
  /**
   * The list of extensions added to the editor by this `Preset`.
   */


  get extensions() {
    return _classPrivateFieldGet(this, _extensions);
  }
  /**
   * The name that the constructor should have, which doesn't get mangled in
   * production builds.
   */


  get constructorName() {
    return "".concat(pascalCase(this.name), "Preset");
  }
  /**
   * The store is a property that's internal to the preset. It include important
   * items like the `view` and `schema` that are added by the editor manager.
   *
   * **NOTE** - The store is not available until the manager has been created and
   * received the extension. As a result trying to access the store during
   * `init` and `constructor` will result in a runtime error.
   */


  get extensionStore() {
    !_classPrivateFieldGet(this, _extensionStore) ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.MANAGER_PHASE_ERROR,
      message: "An error occurred while attempting to access the 'preset.extensionStore' before the `RemirrorManager` has run through it's lifecycle methods."
    }) : invariant(false) : void 0;
    return freeze(_classPrivateFieldGet(this, _extensionStore), {
      requireKeys: true
    });
  }
  /**
   * This store is can be modified by the extension manager with and lifecycle
   * extension methods.
   *
   * Different properties are added at different times so it's important to
   * check the documentation for each property to know what phase is being
   * added.
   */


  constructor() {
    for (var _len = arguments.length, parameters = new Array(_len), _key = 0; _key < _len; _key++) {
      parameters[_key] = arguments[_key];
    }

    super({
      validator: isValidPresetConstructor,
      defaultOptions: {},
      code: ErrorConstant.INVALID_PRESET
    }, ...parameters); // Create the extension list.

    _extensionStore.set(this, {
      writable: true,
      value: void 0
    });

    _extensions.set(this, {
      writable: true,
      value: void 0
    });

    _extensionMap.set(this, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldSet(this, _extensions, uniqueBy(this.createExtensions(), // Ensure that all the provided extensions are unique.
    extension => extension.constructor));

    _classPrivateFieldSet(this, _extensionMap, new Map()); // Create the extension map for retrieving extensions from the `Preset`


    var _iterator = _createForOfIteratorHelper(_classPrivateFieldGet(this, _extensions)),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var extension = _step.value;

        _classPrivateFieldGet(this, _extensionMap).set(extension.constructor, extension);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }
  /**
   * Check if the type of this extension's constructor matches the type of the
   * provided constructor.
   */


  isOfType(Constructor) {
    return this.constructor === Constructor;
  }
  /**
   * Create the extensions which will be consumed by the preset.
   *
   * Since this method is called in the constructor it should always be created
   * as an instance method and not a property. Properties aren't available for
   * the call to the parent class.
   *
   * ```ts
   * class MyPreset extends Preset {
   *   // GOOD ✅
   *   createExtensions() {
   *     return [];
   *   }
   *
   *   // BAD ❌
   *   createExtensions = () => {
   *     return [];
   *   }
   * }
   * ```
   */


  /**
   * When there are duplicate extensions used within the editor the extension
   * manager will call this method and make sure all presets are using the same
   * instance of the `ExtensionConstructor`.
   */
  replaceExtension(constructor, extension) {
    if (_classPrivateFieldGet(this, _extensionMap).has(constructor)) {
      _classPrivateFieldGet(this, _extensionMap).set(constructor, extension);
    }
  }
  /**
   * Get an extension from the extension holder (either a preset or a manager)
   * that corresponds to the provided `Constructor`.
   *
   * @param Constructor - the extension constructor to find in the editor.
   *
   * @remarks
   *
   * This method will throw an error if the constructor doesn't exist.
   */


  getExtension(Constructor) {
    var extension = _classPrivateFieldGet(this, _extensionMap).get(Constructor); // Throws an error if attempting to get an extension which is not available
    // in this preset.


    !extension ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.INVALID_PRESET_EXTENSION,
      message: "'".concat(Constructor.name, "' does not exist within the preset: '").concat(this.name, "'")
    }) : invariant(false) : void 0;
    return extension;
  }
  /**
   * Pass a reference to the globally shared `ExtensionStore` for this extension.
   *
   * @remarks
   *
   * The extension store allows extensions to access important variables without
   * complicating their creator methods.
   *
   * ```ts
   * import { PlainExtension } from 'remirror/core';
   *
   * class Awesome extends PlainExtension {
   *   customMethod() {
   *     if (this.store.view.hasFocus()) {
   *       log('dance dance dance');
   *     }
   *   }
   * }
   * ```
   *
   * This should only be called by the `RemirrorManager`.
   *
   * @internal
   * @nonVirtual
   */


  setExtensionStore(store) {
    if (_classPrivateFieldGet(this, _extensionStore)) {
      return;
    }

    _classPrivateFieldSet(this, _extensionStore, store);
  }
  /**
   * Clone a preset.
   */


  clone() {
    for (var _len2 = arguments.length, parameters = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      parameters[_key2] = arguments[_key2];
    }

    return new this.constructor(...parameters);
  }

}

_defineProperty(Preset, "defaultOptions", {});

/**
 * Determines if the passed in value is a preset.
 *
 * @param value - the preset to check
 */
function isPreset(value) {
  return isRemirrorType(value) && isIdentifierOfType(value, RemirrorIdentifier.Preset);
}
/**
 * Determines if the passed in value is a preset constructor.
 *
 * @param value - the value to test
 */

function isPresetConstructor(value) {
  return isRemirrorType(value) && isIdentifierOfType(value, RemirrorIdentifier.PresetConstructor);
}
/**
 * Checks that the preset has a valid constructor with the `defaultOptions` and
 * `defaultOptions` defined as static properties.
 */

function isValidPresetConstructor(Constructor) {
  var code = ErrorConstant.INVALID_PRESET;
  !isPresetConstructor(Constructor) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    message: 'This is not a valid extension constructor',
    code
  }) : invariant(false) : void 0;
  isValidConstructor(Constructor, code);
}
/**
 * Automatically infers whether the constructor parameter is required for the
 * Preset.
 *
 * - Required when any of the options are not optional.
 */

/**
 * The parameter that is passed into `DelayedCommand`s.
 */

/**
 * Returns `true` when the provided value is a delayed value.
 */
function isDelayedValue(value) {
  return isFunction(value) || isPromise(value);
}
/**
 * Add tentative support for delayed commands in the editor.
 *
 * Delayed commands are commands that run an immediate action, like adding a
 * tracker to a position in the document. Once the promise that is provided is
 * returned the `onDone` parameter is run with the document in the current
 * state. The tracker that was added can now be used to insert content, delete
 * content or replace content.
 *
 * @experimental This is still being worked on and the API is subject to changes
 * in structure going forward.
 *
 */

function delayedCommand(_ref) {
  var immediate = _ref.immediate,
      promise = _ref.promise,
      onDone = _ref.onDone,
      onFail = _ref.onFail;
  return parameter => {
    var view = parameter.view;

    if ((immediate === null || immediate === void 0 ? void 0 : immediate(parameter)) === false) {
      return false;
    }

    if (!view) {
      return true;
    }

    var deferred = isFunction(promise) ? promise() : promise;
    deferred.then(value => {
      // Run the command
      onDone({
        state: view.state,
        tr: view.state.tr,
        dispatch: view.dispatch,
        view,
        value
      });
    }).catch(() => {
      // Run the failure command if it exists.
      onFail === null || onFail === void 0 ? void 0 : onFail({
        state: view.state,
        tr: view.state.tr,
        dispatch: view.dispatch,
        view
      });
    });
    return true;
  };
}

/**
 * A custom `toggleMark` function that works for the `remirror` codebase.
 *
 * Create a command function that toggles the given mark with the given
 * attributes. Will return `false` when the current selection doesn't support
 * that mark. This will remove the mark if any marks of that type exist in the
 * selection, or add it otherwise. If the selection is empty, this applies to
 * the [stored marks](#state.EditorState.storedMarks) instead of a range of the
 * document.
 *
 * The differences from the `prosemirror-commands` version.
 * - Acts on the transaction rather than the state to allow for commands to be
 *   chained together.
 * - Uses the ONE parameter function signature for compatibility with remirror.
 * - Supports passing a custom range.
 */
function toggleMark(parameter) {
  var type = parameter.type,
      attrs = parameter.attrs,
      range = parameter.range;
  return parameter => {
    var dispatch = parameter.dispatch,
        tr = parameter.tr;

    if (range) {
      isMarkActive(_objectSpread({
        trState: tr,
        type
      }, range)) ? dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.removeMark(range.from, range.to, type)) : dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.addMark(range.from, range.to, type.create(attrs)));
      return true;
    }

    return convertCommand(toggleMark$1(type, attrs))(parameter);
  };
}

/**
 * Insert text into the dom at the current location by default. If a promise is
 * provided then the text will be inserted at the tracked position when the
 * promise is resolved.
 */
function insertText(text) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return (_ref2) => {
    var tr = _ref2.tr,
        dispatch = _ref2.dispatch,
        state = _ref2.state;
    var schema = state.schema;
    var selection = tr.selection;
    var _options$from = options.from,
        from = _options$from === void 0 ? selection.from : _options$from,
        _options$to = options.to,
        to = _options$to === void 0 ? from !== null && from !== void 0 ? from : selection.to : _options$to,
        _options$marks = options.marks,
        marks = _options$marks === void 0 ? {} : _options$marks;

    if (!dispatch) {
      return true;
    } // Insert the text


    tr.insertText(text, from, to); // Map the end position after inserting the text to understand what needs to
    // be wrapped with a mark.

    var end = tr.steps[tr.steps.length - 1].getMap().map(to); // Loop through the provided marks to add the mark to the selection. This
    // uses the order of the map you created. If any marks are exclusive, they
    // will override the previous.

    var _iterator = _createForOfIteratorHelper(entries(marks)),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _step$value = _slicedToArray(_step.value, 2),
            markName = _step$value[0],
            attributes = _step$value[1];

        tr.addMark(from, end, schema.marks[markName].create(attributes));
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    dispatch(tr);
    return true;
  };
}

var _dec, _class, _transaction, _customTransaction, _temp;
var CommandsExtension = (_dec = /*#__PURE__*/extensionDecorator({
  defaultPriority: ExtensionPriority.Highest,
  defaultOptions: {
    trackerClassName: 'remirror-tracker-position',
    trackerNodeName: 'span'
  },
  staticKeys: ['trackerClassName', 'trackerNodeName']
}), /*#__PURE__*/_dec(_class = (_temp = (_transaction = /*#__PURE__*/new WeakMap(), _customTransaction = /*#__PURE__*/new WeakMap(), class CommandsExtension extends PlainExtension {
  constructor() {
    var _this;

    super(...arguments);
    _this = this;

    _transaction.set(this, {
      writable: true,
      value: void 0
    });

    _customTransaction.set(this, {
      writable: true,
      value: void 0
    });

    _defineProperty(this, "forceUpdate", function (tr) {
      var _this$getMeta = _this.getMeta(tr),
          forcedUpdates = _this$getMeta.forcedUpdates;

      for (var _len = arguments.length, keys = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        keys[_key - 1] = arguments[_key];
      }

      _this.setMeta(tr, {
        forcedUpdates: uniqueArray([...forcedUpdates, ...keys])
      });

      return tr;
    });

    _defineProperty(this, "getForcedUpdates", tr => {
      return this.getMeta(tr).forcedUpdates;
    });
  }

  get name() {
    return 'commands';
  }
  /**
   * The current transaction which allows for making commands chainable.
   *
   * It is shared by all the commands helpers and can even be used in the
   * [[`KeymapExtension`]].
   */


  get transaction() {
    if (_classPrivateFieldGet(this, _customTransaction)) {
      return _classPrivateFieldGet(this, _customTransaction);
    } // Make sure we have the most up to date state.


    var state = this.store.getState();

    if (!_classPrivateFieldGet(this, _transaction)) {
      // Since there is currently no transaction set, make sure to create a new
      // one. Behind the scenes `state.tr` creates a new transaction for us to
      // use.
      _classPrivateFieldSet(this, _transaction, state.tr);
    } // Check that the current transaction is valid.


    var isValid = _classPrivateFieldGet(this, _transaction).before.eq(state.doc); // Check whether the current transaction has any already applied to it.


    var hasSteps = !isEmptyArray(_classPrivateFieldGet(this, _transaction).steps);

    if (!isValid) {
      // Since the transaction is not valid we create a new one to prevent any
      // `mismatched` transaction errors.
      var _tr = state.tr; // Now checking if any steps had been added to the previous transaction
      // and adding them to the newly created transaction.

      if (hasSteps) {
        var _iterator = _createForOfIteratorHelper(_classPrivateFieldGet(this, _transaction).steps),
            _step;

        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var step = _step.value;

            _tr.step(step);
          }
        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }
      } // Make sure to store the transaction value to the instance of this
      // extension.


      _classPrivateFieldSet(this, _transaction, _tr);
    }

    return _classPrivateFieldGet(this, _transaction);
  }
  /**
   * This is the holder for the shared transaction which is shared by commands
   * in order to support chaining.
   */


  onCreate() {
    var _this$store = this.store,
        setExtensionStore = _this$store.setExtensionStore,
        setStoreKey = _this$store.setStoreKey; // Support forced updates.

    setExtensionStore('forceUpdate', this.forceUpdate);
    setStoreKey('getForcedUpdates', this.getForcedUpdates); // Enable retrieval of the current transaction.

    setExtensionStore('getTransaction', () => this.transaction); // Enable retrieval of the command parameter.

    setExtensionStore('getCommandParameter', () => ({
      tr: this.transaction,
      dispatch: this.store.view.dispatch,
      state: this.store.view.state,
      view: this.store.view
    }));
  }
  /**
   * Commands are only available after the view is attached.
   *
   * This is where they are attached.
   */


  onView(view) {
    var _this$store2 = this.store,
        setStoreKey = _this$store2.setStoreKey,
        setExtensionStore = _this$store2.setExtensionStore;
    var commands = object();
    var names = new Set();
    var chained = object();
    var unchained = object();
    var original = object();

    var _iterator2 = _createForOfIteratorHelper(this.store.extensions),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var extension = _step2.value;

        // There's no need to continue if the extension has no commands.
        if (!extension.createCommands) {
          continue;
        } // Gather the returned commands object from the extension.


        this.addCommands({
          names,
          chained,
          unchained,
          original,
          commands: extension.createCommands()
        });
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }

    var _iterator3 = _createForOfIteratorHelper(entries(unchained)),
        _step3;

    try {
      for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
        var _step3$value = _slicedToArray(_step3.value, 2),
            commandName = _step3$value[0],
            _step3$value$ = _step3$value[1],
            command = _step3$value$.command,
            isEnabled = _step3$value$.isEnabled;

        commands[commandName] = command;
        commands[commandName].isEnabled = isEnabled;
      }
    } catch (err) {
      _iterator3.e(err);
    } finally {
      _iterator3.f();
    }

    chained.run = () => view.dispatch(this.transaction);

    setStoreKey('rawCommands', original);
    setStoreKey('commands', commands);
    setStoreKey('chain', chained);
    setExtensionStore('rawCommands', original);
    setExtensionStore('commands', commands);
    setExtensionStore('chain', chained);
  }
  /**
   * Update the cached transaction whenever the state is updated.
   */


  onStateUpdate(_ref) {
    var state = _ref.state;

    _classPrivateFieldSet(this, _customTransaction, undefined);

    _classPrivateFieldSet(this, _transaction, state.tr);
  }

  createHelpers() {
    return {
      /**
       * Find the position for the tracker with the ID specified.
       *
       * @param id - the unique position id which can be any type
       * @param state - an optional state. Defaults to using the current state.
       */
      findPositionTracker: (id, state) => this.findPositionTracker(id, state),

      /**
       * Find the positions of all the trackers in document.
       *
       * @param state - an optional state. Defaults to using the current state.
       */
      findAllPositionTrackers: state => this.findAllPositionTrackers(state)
    };
  }
  /**
   * Create the default commands available to all extensions.
   */


  createCommands() {
    var _this2 = this;

    var commands = {
      /**
       * Enable custom commands to be used within the editor by users.
       *
       * This is preferred to the initial idea of setting commands on the
       * manager or even as a prop. The problem is that there's no typechecking
       * and it should be just fine to add your custom commands here to see the
       * dispatched immediately.
       *
       * To use it, firstly define the command.
       *
       * ```ts
       * import { CommandFunction } from 'remirror/core';
       *
       * const myCustomCommand: CommandFunction = ({ tr, dispatch }) => {
       *   dispatch?.(tr.insertText('My Custom Command'));
       *
       *   return true;
       * }
       * ```
       *
       * And then use it within the component.
       *
       * ```ts
       * import React, { useCallback } from 'react';
       * import { useRemirror } from 'remirror/react';
       *
       * const MyEditorButton = () => {
       *   const { commands } = useRemirror();
       *   const onClick = useCallback(() => {
       *     commands.customDispatch(myCustomCommand);
       *   }, [commands])
       *
       *   return <button onClick={onClick}>Custom Command</button>
       * }
       * ```
       *
       * An alternative is to use a custom command directly from a
       * `prosemirror-*` library. This can be accomplished in the following way.
       *
       *
       * ```ts
       * import { joinDown } from 'prosemirror-commands';
       * import { convertCommand } from 'remirror/core';
       *
       * const MyEditorButton = () => {
       *   const { commands } = useRemirror();
       *   const onClick = useCallback(() => {
       *     commands.customDispatch(convertCommand(joinDown));
       *   }, [commands]);
       *
       *   return <button onClick={onClick}>Custom Command</button>;
       * };
       * ```
       */
      customDispatch(command) {
        return command;
      },

      /**
       * Create a custom transaction.
       *
       * Use the command at the beginning of the command chain to override the
       * shared transaction.
       *
       * There are times when you want to be sure of the transaction which is
       * being updated.
       *
       * To restore the previous transaction call the `restore` chained method.
       *
       * @param tr - the transaction to set
       *
       * @remarks
       *
       * This is only intended for use within a chainable command chain.
       *
       * You **MUST** call the `restore` command after using this to prevent
       * cryptic errors.
       */
      custom: tr => {
        return () => {
          _classPrivateFieldSet(this, _customTransaction, tr);

          return true;
        };
      },
      restore: () => {
        return () => {
          _classPrivateFieldSet(this, _customTransaction, undefined);

          return true;
        };
      },

      /**
       * Insert text into the dom at the current location by default. If a
       * promise is provided instead of text the resolved value will be inserted
       * at the tracked position.
       */
      insertText: function insertText$1(text) {
        var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
        return isDelayedValue(text) ? delayedCommand({
          promise: text,
          immediate: commands.addPositionTracker(_objectSpread({
            id: text
          }, options)),
          onDone: (_ref2) => {
            var value = _ref2.value,
                rest = _objectWithoutProperties(_ref2, ["value"]);

            var range = _this2.findPositionTracker(text);

            _this2.removePositionTracker({
              id: text,
              tr: rest.tr
            });

            if (!range) {
              return false;
            }

            return commands.insertText(value, range)(rest);
          },
          // Cleanup in case of an error.
          onFail: commands.removePositionTracker(text)
        }) : insertText(text, options);
      },

      /**
       * Select the text within the provided range.
       */
      selectText: selection => (_ref3) => {
        var tr = _ref3.tr,
            dispatch = _ref3.dispatch;
        var textSelection = getTextSelection(selection, tr.doc); // TODO: add some safety checks here. If the selection is out of range
        // perhaps silently fail

        dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setSelection(textSelection));
        return true;
      },

      /**
       * Delete the provided range or current selection.
       */
      delete(range) {
        return (_ref4) => {
          var tr = _ref4.tr,
              dispatch = _ref4.dispatch;

          var _ref5 = range !== null && range !== void 0 ? range : tr.selection,
              from = _ref5.from,
              to = _ref5.to;

          dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.delete(from, to));
          return true;
        };
      },

      /**
       * Fire an empty update to trigger an update to all decorations, and state
       * that may not yet have run.
       *
       * This can be used in extensions to trigger updates certain options that
       * affect the editor state have updated.
       */
      emptyUpdate: () => {
        return (_ref6) => {
          var tr = _ref6.tr,
              dispatch = _ref6.dispatch;
          dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr);
          return true;
        };
      },

      /**
       * Force an update of the specific
       */
      forceUpdate: function forceUpdate() {
        for (var _len2 = arguments.length, keys = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
          keys[_key2] = arguments[_key2];
        }

        return (_ref7) => {
          var tr = _ref7.tr,
              dispatch = _ref7.dispatch;
          dispatch === null || dispatch === void 0 ? void 0 : dispatch(_this2.forceUpdate(tr, ...keys));
          return true;
        };
      },

      /**
       * Update the attributes for the node at the specified `pos` in the
       * editor.
       */
      updateNodeAttributes: (pos, attrs) => {
        return (_ref8) => {
          var tr = _ref8.tr,
              dispatch = _ref8.dispatch;
          dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setNodeMarkup(pos, undefined, attrs));
          return true;
        };
      },

      /**
       * Fire an update to remove the current range selection. The cursor will
       * be placed at the anchor of the current range selection.
       *
       * A range selection is a non-empty text selection.
       */
      emptySelection: () => {
        return (_ref9) => {
          var tr = _ref9.tr,
              dispatch = _ref9.dispatch;
          var selection = tr.selection;

          if (selection.empty) {
            return false;
          }

          dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setSelection(TextSelection.create(tr.doc, tr.selection.anchor)));
          return true;
        };
      },

      /**
       * Command to dispatch a transaction adding the tracker position to be
       * tracked.
       *
       * @param id - the value that is used to identify this tracker. This can
       * be any value. A promise, a function call, a string.
       * @param options - the options to call the tracked position with. You can
       * specify the range `{ from: number; to: number }` as well as the class
       * name.
       */
      addPositionTracker: tracker => (_ref10) => {
        var dispatch = _ref10.dispatch,
            tr = _ref10.tr;
        return this.addPositionTracker(_objectSpread(_objectSpread({}, tracker), {}, {
          tr,
          checkOnly: !dispatch
        })) ? (dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr), true) : false;
      },

      /**
       * A command to remove the specified tracker position.
       */
      removePositionTracker: id => (_ref11) => {
        var dispatch = _ref11.dispatch,
            tr = _ref11.tr;
        return this.removePositionTracker({
          id,
          tr,
          checkOnly: !dispatch
        }) ? (dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr), true) : false;
      },

      /**
       * A command to remove all active tracker positions.
       */
      clearPositionTrackers: () => (_ref12) => {
        var tr = _ref12.tr,
            dispatch = _ref12.dispatch;
        return this.clearPositionTrackers({
          tr,
          checkOnly: !dispatch
        }) ? (dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr), true) : false;
      }
    };
    return commands;
  }
  /**
   * This plugin stores all tracked positions in the editor and maps them via
   * the transaction updates.
   */


  createPlugin() {
    var _this3 = this;

    return {
      state: {
        init: () => {
          return DecorationSet.empty;
        },
        apply: (tr, decorationSet) => {
          // Map the decoration based on the changes to the document.
          decorationSet = decorationSet.map(tr.mapping, tr.doc); // Get tracker updates from the meta data

          var _this$getMeta2 = this.getMeta(tr),
              added = _this$getMeta2.added,
              clearTrackers = _this$getMeta2.clearTrackers,
              removed = _this$getMeta2.removed;

          if (clearTrackers) {
            return DecorationSet.empty;
          } // Update the decorations with any added position trackers.


          var _iterator4 = _createForOfIteratorHelper(added),
              _step4;

          try {
            for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
              var add = _step4.value;
              var className = add.className,
                  nodeName = add.nodeName,
                  id = add.id,
                  from = add.from,
                  to = add.to,
                  pos = add.pos,
                  node = add.node;
              var deco = void 0;

              if (isNumber(pos)) {
                // It's up to the user to ensure the position exists before
                // adding.
                var $pos = tr.doc.resolve(pos);

                if ($pos.nodeAfter) {
                  deco = Decoration.node(pos, pos + $pos.nodeAfter.nodeSize, {
                    nodeName,
                    class: className
                  }, {
                    id,
                    type: this.name
                  });
                } else {
                  // TODO it would nice to cancel the whole tracker when node not
                  // found.
                  // Since is a pos was provided there's no need to check anything
                  // else in this block.
                  continue;
                }
              } else if (node) {
                // In this block find the parent node of the current `from`
                // position and use that to mark the decoration.
                var _$pos = tr.doc.resolve(from);

                var found = findNodeAtPosition(_$pos);

                if (found) {
                  deco = Decoration.node(found.pos, found.end, {
                    nodeName,
                    class: className
                  }, {
                    id,
                    type: this.name
                  });
                } else {
                  // TODO it would nice to cancel the whole tracker when node not
                  // found.
                  // Don't add a decoration since the user specified that this
                  // should be a node
                  continue;
                }
              } else if (from === to) {
                // Add this as a widget if the range is empty.
                var widget = document.createElement(nodeName);
                widget.classList.add(className);
                deco = Decoration.widget(from, document.createElement(nodeName), {
                  id,
                  type: this.name
                });
              } else {
                // Make this span across nodes if the range is not empty.
                deco = Decoration.inline(from, to, {
                  nodeName,
                  class: className
                }, {
                  id,
                  type: this.name
                });
              }

              decorationSet = decorationSet.add(tr.doc, [deco]);
            }
          } catch (err) {
            _iterator4.e(err);
          } finally {
            _iterator4.f();
          }

          var _iterator5 = _createForOfIteratorHelper(removed),
              _step5;

          try {
            var _loop = function _loop() {
              var id = _step5.value;
              var found = decorationSet.find(undefined, undefined, spec => spec.id === id && spec.type === _this3.name);
              decorationSet = decorationSet.remove(found);
            };

            for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
              _loop();
            }
          } catch (err) {
            _iterator5.e(err);
          } finally {
            _iterator5.f();
          }

          return decorationSet;
        }
      },
      props: {
        decorations: state => {
          return this.getPluginState(state);
        }
      }
    };
  }
  /**
   * A helper for forcing through updates in the view layer. The view layer can
   * check for the meta data of the transaction with
   * `manager.store.getForcedUpdate(tr)`. If that has a value then it should use
   * the unique symbol to update the key.
   */


  /**
   * Get the command metadata
   */
  getMeta(tr) {
    var _tr$getMeta;

    var meta = (_tr$getMeta = tr.getMeta(this.pluginKey)) !== null && _tr$getMeta !== void 0 ? _tr$getMeta : {};
    return _objectSpread(_objectSpread({}, DEFAULT_COMMAND_META), meta);
  }

  setMeta(tr, update) {
    var meta = this.getMeta(tr);
    tr.setMeta(this.pluginKey, _objectSpread(_objectSpread({}, meta), update));
  }
  /**
   * Add the commands from the provided `commands` property to the `chained`,
   * `original` and `unchained` objects.
   */


  addCommands(parameter) {
    var chained = parameter.chained,
        commands = parameter.commands,
        names = parameter.names,
        unchained = parameter.unchained,
        original = parameter.original;

    var _iterator6 = _createForOfIteratorHelper(entries(commands)),
        _step6;

    try {
      for (_iterator6.s(); !(_step6 = _iterator6.n()).done;) {
        var _step6$value = _slicedToArray(_step6.value, 2),
            name = _step6$value[0],
            command = _step6$value[1];

        // Command names must be unique.
        throwIfNameNotUnique({
          name,
          set: names,
          code: ErrorConstant.DUPLICATE_COMMAND_NAMES
        }); // Make sure the command name is not forbidden.

        !!forbiddenNames.has(name) ? process.env.NODE_ENV !== "production" ? invariant(false, {
          code: ErrorConstant.DUPLICATE_COMMAND_NAMES,
          message: 'The command name you chose is forbidden.'
        }) : invariant(false) : void 0; // Store the original command.

        original[name] = command;
        unchained[name] = {
          command: this.unchainedFactory({
            command
          }),
          isEnabled: this.unchainedFactory({
            command,
            shouldDispatch: false
          })
        };
        chained[name] = this.chainedFactory({
          command,
          chained
        });
      }
    } catch (err) {
      _iterator6.e(err);
    } finally {
      _iterator6.f();
    }
  }
  /**
   * Create an unchained command method.
   */


  unchainedFactory(parameter) {
    var _this4 = this;

    return function () {
      var _parameter$shouldDisp = parameter.shouldDispatch,
          shouldDispatch = _parameter$shouldDisp === void 0 ? true : _parameter$shouldDisp,
          command = parameter.command;
      var view = _this4.store.view;
      var state = view.state;
      var dispatch;

      if (shouldDispatch) {
        dispatch = view.dispatch;
      }

      return command(...arguments)({
        state,
        dispatch,
        view,
        tr: _this4.transaction
      });
    };
  }
  /**
   * Create a chained command method.
   */


  chainedFactory(parameter) {
    var _this5 = this;

    return function () {
      var chained = parameter.chained,
          command = parameter.command;
      var view = _this5.store.view;
      var state = view.state;
      /**
       * This function is used in place of the `view.dispatch` method which is
       * passed through to all commands.
       *
       * It is responsible for checking that the transaction which was
       * dispatched is the same as the shared transaction which makes chainable
       * commands possible.
       */

      var dispatch = transaction => {
        // Throw an error if the transaction being dispatched is not the same as
        // the currently stored transaction.
        !(transaction === _this5.transaction) ? process.env.NODE_ENV !== "production" ? invariant(false, {
          message: 'Chaining currently only supports `CommandFunction` methods which do not use the `state.tr` property. Instead you should use the provided `tr` property.'
        }) : invariant(false) : void 0;
      };

      command(...arguments)({
        state,
        dispatch,
        view,
        tr: _this5.transaction
      });
      return chained;
    };
  }
  /**
   * Add a tracker position with the specified params to the transaction and
   * return the transaction.
   *
   * It is up to you to dispatch the transaction or you can just use the
   * commands.
   */


  addPositionTracker(parameter) {
    var tr = parameter.tr,
        _parameter$checkOnly = parameter.checkOnly,
        checkOnly = _parameter$checkOnly === void 0 ? false : _parameter$checkOnly,
        id = parameter.id,
        rest = _objectWithoutProperties(parameter, ["tr", "checkOnly", "id"]);

    var existingPosition = this.findPositionTracker(id);

    if (existingPosition) {
      return false;
    }

    if (checkOnly) {
      return true;
    }

    var _this$getMeta3 = this.getMeta(tr),
        added = _this$getMeta3.added;

    var _this$options = this.options,
        trackerClassName = _this$options.trackerClassName,
        trackerNodeName = _this$options.trackerNodeName;
    var from = rest.from,
        to = rest.to,
        className = rest.className,
        _rest$nodeName = rest.nodeName,
        nodeName = _rest$nodeName === void 0 ? trackerNodeName : _rest$nodeName,
        _rest$pos = rest.pos,
        pos = _rest$pos === void 0 ? null : _rest$pos,
        _rest$node = rest.node,
        node = _rest$node === void 0 ? false : _rest$node;
    var classes = (className ? [trackerClassName, className] : [trackerClassName]).join(' ');
    this.setMeta(tr, {
      added: [...added, {
        id,
        nodeName,
        pos,
        node,
        from: isNumber(from) ? from : tr.selection.from,
        to: isNumber(to) ? to : tr.selection.to,
        className: classes
      }]
    });
    return true;
  }
  /**
   * Discards a previously defined tracker once not needed.
   *
   * This should be used to cleanup once the position is no longer needed.
   */


  removePositionTracker(parameter) {
    var id = parameter.id,
        tr = parameter.tr,
        _parameter$checkOnly2 = parameter.checkOnly,
        checkOnly = _parameter$checkOnly2 === void 0 ? false : _parameter$checkOnly2;
    var existingPosition = this.findPositionTracker(id);

    if (!existingPosition) {
      return false;
    }

    if (checkOnly) {
      return true;
    }

    var _this$getMeta4 = this.getMeta(tr),
        removed = _this$getMeta4.removed;

    this.setMeta(tr, {
      removed: uniqueArray([...removed, id])
    });
    return true;
  }
  /**
   * This helper returns a transaction that clears all position trackers when
   * any exist.
   *
   * Otherwise it returns undefined.
   */


  clearPositionTrackers(parameter) {
    var tr = parameter.tr,
        _parameter$checkOnly3 = parameter.checkOnly,
        checkOnly = _parameter$checkOnly3 === void 0 ? false : _parameter$checkOnly3;
    var positionTrackerState = this.getPluginState();

    if (positionTrackerState === DecorationSet.empty) {
      return false;
    }

    if (checkOnly) {
      return true;
    }

    this.setMeta(tr, {
      clearTrackers: true
    });
    return true;
  }
  /**
   * Find the position for the tracker with the ID specified.
   *
   * @param id - the unique position id which can be any type
   * @param state - an optional state. Defaults to using the current state.
   */


  findPositionTracker(id, state) {
    return this.findAllPositionTrackers(state).get(id);
  }
  /**
   * Find the positions of all the trackers in document.
   *
   * @param state - an optional state. Defaults to using the current state.
   */


  findAllPositionTrackers(state) {
    var trackers = new Map();
    var decorations = this.getPluginState(state);
    var found = decorations.find(undefined, undefined, spec => spec.type === this.name);

    var _iterator7 = _createForOfIteratorHelper(found),
        _step7;

    try {
      for (_iterator7.s(); !(_step7 = _iterator7.n()).done;) {
        var decoration = _step7.value;
        trackers.set(decoration.spec.id, {
          from: decoration.from,
          to: decoration.to
        });
      }
    } catch (err) {
      _iterator7.e(err);
    } finally {
      _iterator7.f();
    }

    return trackers;
  }

}), _temp)) || _class);
var DEFAULT_COMMAND_META = {
  added: [],
  clearTrackers: false,
  forcedUpdates: [],
  removed: []
};
/**
 * Provides the list of Prosemirror EditorView props that should be updated/
 */

/**
 * The names that are forbidden from being used as a command name.
 */
var forbiddenNames = /*#__PURE__*/new Set(['run', 'chain', 'original', 'raw']);

var _dec$1, _class$1;
var HelpersExtension = (_dec$1 = /*#__PURE__*/extensionDecorator({
  defaultPriority: ExtensionPriority.High
}), /*#__PURE__*/_dec$1(_class$1 = class HelpersExtension extends PlainExtension {
  get name() {
    return 'helpers';
  }
  /**
   * Helpers are only available once the view has been added to
   * `RemirrorManager`.
   */


  onView() {
    var _this = this;

    var helpers = object();
    var active = object();
    var names = new Set();

    var _iterator = _createForOfIteratorHelper(this.store.extensions),
        _step;

    try {
      var _loop = function _loop() {
        var extension = _step.value;

        if (isNodeExtension(extension)) {
          active[extension.name] = attrs => {
            return isNodeActive({
              state: _this.store.getState(),
              type: extension.type,
              attrs
            });
          };
        }

        if (isMarkExtension(extension)) {
          active[extension.name] = () => {
            return isMarkActive({
              trState: _this.store.getState(),
              type: extension.type
            });
          };
        }

        if (!extension.createHelpers) {
          return "continue";
        }

        var extensionHelpers = extension.createHelpers();

        var _iterator2 = _createForOfIteratorHelper(entries(extensionHelpers)),
            _step2;

        try {
          for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
            var _step2$value = _slicedToArray(_step2.value, 2),
                name = _step2$value[0],
                helper = _step2$value[1];

            throwIfNameNotUnique({
              name,
              set: names,
              code: ErrorConstant.DUPLICATE_HELPER_NAMES
            });
            helpers[name] = helper;
          }
        } catch (err) {
          _iterator2.e(err);
        } finally {
          _iterator2.f();
        }
      };

      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _ret = _loop();

        if (_ret === "continue") continue;
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    this.store.setStoreKey('active', active);
    this.store.setStoreKey('helpers', helpers);
    this.store.setExtensionStore('helpers', helpers);
  }

  createHelpers() {
    return {
      /**
       * Check whether the selection is empty.
       */
      isSelectionEmpty: () => isSelectionEmpty(this.store.view.state)
    };
  }

}) || _class$1);

var _dec$2, _class$2, _temp$1;
var InputRulesExtension = (_dec$2 = /*#__PURE__*/extensionDecorator({
  defaultPriority: ExtensionPriority.High,
  handlerKeys: ['shouldSkipInputRule'],
  // Return when the value `true` is encountered.
  handlerKeyOptions: {
    shouldSkipInputRule: {
      earlyReturnValue: true
    }
  }
}), /*#__PURE__*/_dec$2(_class$2 = (_temp$1 = class InputRulesExtension extends PlainExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "rebuildInputRules", () => {
      var previousInputRules = this.inputRulesPlugin;
      this.loopExtensions();
      this.store.replacePlugin(previousInputRules, this.inputRulesPlugin);
      this.store.reconfigureStatePlugins();
    });
  }

  get name() {
    return 'inputRules';
  }

  /**
   * Ensure that all ssr transformers are run.
   */
  onCreate() {
    this.store.setExtensionStore('rebuildInputRules', this.rebuildInputRules);
    this.loopExtensions();
    this.store.addPlugins(this.inputRulesPlugin);
  }

  loopExtensions() {
    var rules = [];
    var invalidMarks = this.store.markTags[ExtensionTag.ExcludeInputRules];

    var _iterator = _createForOfIteratorHelper(this.store.extensions),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _this$store$managerSe, _extension$options$ex;

        var extension = _step.value;

        if ( // managerSettings excluded this from running
        ((_this$store$managerSe = this.store.managerSettings.exclude) === null || _this$store$managerSe === void 0 ? void 0 : _this$store$managerSe.inputRules) || // Method doesn't exist
        !extension.createInputRules || ( // Extension settings exclude it
        (_extension$options$ex = extension.options.exclude) === null || _extension$options$ex === void 0 ? void 0 : _extension$options$ex.inputRules)) {
          continue;
        } // For each input rule returned by the extension, add a `shouldSkip`
        // property.


        var _iterator2 = _createForOfIteratorHelper(extension.createInputRules()),
            _step2;

        try {
          for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
            var rule = _step2.value;
            rule.shouldSkip = this.options.shouldSkipInputRule;
            rule.invalidMarks = invalidMarks;
            rules.push(rule);
          }
        } catch (err) {
          _iterator2.e(err);
        } finally {
          _iterator2.f();
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    this.inputRulesPlugin = inputRules({
      rules
    });
  }
  /**
   * The method for rebuilding all the input rules.
   *
   * 1. Rebuild inputRules.
   * 2. Replace the old input rules plugin.
   * 3. Update the plugins used in the state (triggers an editor update).
   */


}, _temp$1)) || _class$2);

var _dec$3, _class$3, _extraKeyBindings, _temp$2;
var KeymapExtension = (_dec$3 = /*#__PURE__*/extensionDecorator({
  defaultPriority: ExtensionPriority.Low,
  defaultOptions: {
    undoInputRuleOnBackspace: true,
    selectParentNodeOnEscape: false,
    excludeBaseKeymap: false,
    exitMarksOnArrowPress: true
  },
  customHandlerKeys: ['keymap']
}), /*#__PURE__*/_dec$3(_class$3 = (_temp$2 = (_extraKeyBindings = /*#__PURE__*/new WeakMap(), class KeymapExtension extends PlainExtension {
  constructor() {
    super(...arguments);

    _extraKeyBindings.set(this, {
      writable: true,
      value: []
    });

    _defineProperty(this, "rebuildKeymap", () => {
      var previousKeymap = this.keymap;
      this.loopExtensions();
      this.store.replacePlugin(previousKeymap, this.keymap);
      this.store.reconfigureStatePlugins();
    });

    _defineProperty(this, "onAddCustomHandler", (_ref) => {
      var _this$store$rebuildKe, _this$store;

      var keymap = _ref.keymap;

      if (!keymap) {
        return;
      }

      _classPrivateFieldSet(this, _extraKeyBindings, [..._classPrivateFieldGet(this, _extraKeyBindings), keymap]);

      (_this$store$rebuildKe = (_this$store = this.store).rebuildKeymap) === null || _this$store$rebuildKe === void 0 ? void 0 : _this$store$rebuildKe.call(_this$store);
      return () => {
        var _this$store$rebuildKe2, _this$store2;

        _classPrivateFieldSet(this, _extraKeyBindings, _classPrivateFieldGet(this, _extraKeyBindings).filter(binding => binding !== keymap));

        (_this$store$rebuildKe2 = (_this$store2 = this.store).rebuildKeymap) === null || _this$store$rebuildKe2 === void 0 ? void 0 : _this$store$rebuildKe2.call(_this$store2);
      };
    });
  }

  get name() {
    return 'keymap';
  }
  /**
   * The custom keybindings added by the handlers. In react these can be added
   * via `hooks`.
   */


  /**
   * This adds the `createKeymap` method functionality to all extensions.
   */
  onCreate() {
    this.store.setExtensionStore('rebuildKeymap', this.rebuildKeymap);
    this.loopExtensions();
    this.store.addPlugins(this.keymap);
  }
  /**
   * Updates the stored keymap plugin on this extension.
   */


  loopExtensions() {
    var extensionKeymaps = [];

    var _iterator = _createForOfIteratorHelper(this.store.extensions),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _this$store$managerSe, _extension$options$ex;

        var extension = _step.value;

        // Ignore this extension when.
        if ( // The user doesn't want any keymaps in the editor.
        ((_this$store$managerSe = this.store.managerSettings.exclude) === null || _this$store$managerSe === void 0 ? void 0 : _this$store$managerSe.keymap) || // The extension doesn't have the `createKeymap` method.
        !extension.createKeymap || ( // The extension was configured to ignore the keymap.
        (_extension$options$ex = extension.options.exclude) === null || _extension$options$ex === void 0 ? void 0 : _extension$options$ex.keymap)) {
          continue;
        }

        extensionKeymaps.push(extension.createKeymap());
      } // Sort the keymaps giving keybindings added via the handler a priority over
      // those implemented by extensions.

    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    var sortedKeymaps = this.sortKeymaps([..._classPrivateFieldGet(this, _extraKeyBindings), ...extensionKeymaps]);
    var mappedCommands = mergeProsemirrorKeyBindings(sortedKeymaps);
    this.keymap = keymap(mappedCommands);
  }
  /**
   * The method for rebuilding all the extension keymaps.
   *
   * 1. Rebuild keymaps.
   * 2. Replace the old keymap plugin.
   * 3. Update the plugins used in the state (triggers an editor update).
   */


  /**
   * Create the base keymap and give it a low priority so that all other keymaps
   * override it.
   */
  createKeymap() {
    var _this$options = this.options,
        selectParentNodeOnEscape = _this$options.selectParentNodeOnEscape,
        undoInputRuleOnBackspace = _this$options.undoInputRuleOnBackspace,
        excludeBaseKeymap = _this$options.excludeBaseKeymap,
        exitMarksOnArrowPress = _this$options.exitMarksOnArrowPress;
    var baseKeyBindings = object(); // Only add the base keymap if it is **NOT** excluded.

    if (!excludeBaseKeymap) {
      var _iterator2 = _createForOfIteratorHelper(entries(baseKeymap)),
          _step2;

      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var _step2$value = _slicedToArray(_step2.value, 2),
              key = _step2$value[0],
              value = _step2$value[1];

          baseKeyBindings[key] = convertCommand(value);
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
    } // Automatically remove the input rule when the option is set to true.


    if (undoInputRuleOnBackspace) {
      baseKeyBindings.Backspace = convertCommand(chainCommands(undoInputRule, baseKeymap.Backspace));
    } // Allow escape to select the parent node when set to true.


    if (selectParentNodeOnEscape) {
      baseKeyBindings.Escape = convertCommand(selectParentNode);
    }

    if (exitMarksOnArrowPress) {
      this.addExitMarkHandler(baseKeyBindings);
    }

    return [ExtensionPriority.Low, baseKeyBindings];
  }
  /**
   * Add the arrow press handlers for exiting the mark.
   */


  addExitMarkHandler(bindings) {
    var marks = this.store.markTags[ExtensionTag.MarkSupportsExit];
    bindings.ArrowLeft = exitMarkBackwards(marks);
    bindings.ArrowRight = exitMarkForwards(marks);
  }
  /**
   * @internalremarks Think about the case where bindings are disposed of and
   * then added in a different position in the `extraKeyBindings` array. This is
   * especially pertinent when using hooks.
   */


  /**
   * Handle changes in the dynamic properties.
   */
  onSetOptions(parameter) {
    var changes = parameter.changes;
    var shouldReconfigureBindings = false;

    if (changes.excludeBaseKeymap.changed || changes.selectParentNodeOnEscape.changed || changes.undoInputRuleOnBackspace.changed) {
      shouldReconfigureBindings = true;
    }

    if (shouldReconfigureBindings) {
      var _this$store3, _this$store3$rebuildK;

      (_this$store3 = this.store) === null || _this$store3 === void 0 ? void 0 : (_this$store3$rebuildK = _this$store3.rebuildKeymap) === null || _this$store3$rebuildK === void 0 ? void 0 : _this$store3$rebuildK.call(_this$store3);
    }
  }

  sortKeymaps(bindings) {
    // Sort the bindings.
    return sort(bindings.map(binding => // Make all bindings prioritized a default priority of
    // `ExtensionPriority.Default`
    isArray(binding) ? binding : [ExtensionPriority.Default, binding]), // Sort from highest binding to the lowest.
    (a, z) => z[0] - a[0] // Extract the bindings from the prioritized tuple.
    ).map(binding => binding[1]);
  }

}), _temp$2)) || _class$3);
/**
 * Exits the mark forwards when at the end of a block node.
 */

function exitMarkForwards(marks) {
  var checkForReverse = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  return parameter => {
    if (checkForReverse && shouldReverseDirection(parameter.tr)) {
      return exitMarkForwards(marks, false)(parameter);
    }

    var tr = parameter.tr,
        dispatch = parameter.dispatch,
        state = parameter.state;

    if (!isEndOfNode(tr.selection)) {
      return false;
    }

    var $pos = tr.selection.$from;
    var types = new Set(marks.map(name => state.schema.marks[name]));
    var marksToRemove = $pos.marks().filter(mark => types.has(mark.type));

    if (isEmptyArray(marksToRemove)) {
      return false;
    }

    if (!dispatch) {
      return true;
    }

    var _iterator3 = _createForOfIteratorHelper(marksToRemove),
        _step3;

    try {
      for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
        var mark = _step3.value;
        tr.removeStoredMark(mark);
      }
    } catch (err) {
      _iterator3.e(err);
    } finally {
      _iterator3.f();
    }

    dispatch(tr.insertText(' ', tr.selection.from));
    return true;
  };
}
/**
 * Exit a mark when at the beginning of a block node.
 */


function exitMarkBackwards(marks) {
  var checkForReverse = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  return parameter => {
    if (checkForReverse && shouldReverseDirection(parameter.tr)) {
      return exitMarkForwards(marks, false)(parameter);
    }

    var tr = parameter.tr,
        dispatch = parameter.dispatch,
        state = parameter.state;

    if (!isStartOfNode(tr.selection)) {
      return false;
    }

    var $pos = tr.selection.$from;
    var types = new Set(marks.map(name => state.schema.marks[name])); // Find all the marks to remove

    var marksToRemove = $pos.marks().filter(mark => types.has(mark.type));

    if (isEmptyArray(marksToRemove)) {
      // There are no marks to remove therefore defer to the empty marks to
      // remove array.
      return false;
    }

    if (!dispatch) {
      return true;
    } // Remove all the active marks at the current cursor.


    var _iterator4 = _createForOfIteratorHelper(marksToRemove),
        _step4;

    try {
      for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
        var mark = _step4.value;
        tr.removeStoredMark(mark);
      }
    } catch (err) {
      _iterator4.e(err);
    } finally {
      _iterator4.f();
    }

    dispatch(tr);
    return true;
  };
}
/**
 * Whether the item should reverse the direction provided.
 */


function shouldReverseDirection(tr) {
  return tr.selection.$from.parent.attrs.dir === 'rtl';
}
/**
 * Checks that the selection is an empty text selection at the end of its parent
 * node.
 */


function isEndOfNode(selection) {
  var $pos = selection.$from;
  return selection.empty && isTextSelection(selection) && $pos.parent.type.isBlock && $pos.after() === $pos.pos + 1;
}
/**
 * Checks that the selection is an empty text selection at the start of its
 * parent node.
 */


function isStartOfNode(selection) {
  var $pos = selection.$from;
  return selection.empty && isTextSelection(selection) && $pos.parent.type.isBlock && $pos.before() === $pos.pos - 1;
}
/**
 * KeyBindings as a tuple with priority and the keymap.
 */

/**
 * This extension allows others extension to add the `createNodeView` method
 * for creating nodeViews which alter how the dom is rendered for the node.
 *
 * @remarks
 *
 * This is an example of adding custom functionality to an extension via the
 * `ExtensionParameterMethods`.
 *
 * @builtin
 */
class NodeViewsExtension extends PlainExtension {
  get name() {
    return 'nodeViews';
  }
  /**
   * Ensure that all SSR transformers are run.
   */


  onCreate() {
    var _this$store$managerSe;

    var nodeViewList = [];
    var nodeViews = object();

    var _iterator = _createForOfIteratorHelper(this.store.extensions),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _this$store$managerSe2, _extension$options$ex;

        var extension = _step.value;

        if ( // managerSettings excluded this from running
        ((_this$store$managerSe2 = this.store.managerSettings.exclude) === null || _this$store$managerSe2 === void 0 ? void 0 : _this$store$managerSe2.nodeViews) || // Method doesn't exist
        !extension.createNodeViews || ( // Extension settings exclude it
        (_extension$options$ex = extension.options.exclude) === null || _extension$options$ex === void 0 ? void 0 : _extension$options$ex.nodeViews)) {
          continue;
        }

        var _nodeView = extension.createNodeViews(); // `.unshift` ensures higher priority extensions can overwrite the lower
        // priority nodeViews.


        nodeViewList.unshift(isFunction(_nodeView) ? {
          [extension.name]: _nodeView
        } : _nodeView);
      } // Insert the `nodeViews` provided via the manager.

    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    nodeViewList.unshift((_this$store$managerSe = this.store.managerSettings.nodeViews) !== null && _this$store$managerSe !== void 0 ? _this$store$managerSe : {});

    for (var _i = 0, _nodeViewList = nodeViewList; _i < _nodeViewList.length; _i++) {
      var nodeView = _nodeViewList[_i];
      Object.assign(nodeViews, nodeView);
    }

    this.store.setStoreKey('nodeViews', nodeViews);
  }

}

/**
 * This extension allows others extension to add the `createPasteRules` method
 * for automatically transforming pasted text which matches a certain regex
 * pattern in the dom.
 *
 * @builtin
 */

class PasteRulesExtension extends PlainExtension {
  get name() {
    return 'pasteRules';
  }
  /**
   * Ensure that all ssr transformers are run.
   */


  onCreate() {
    var pasteRules = [];

    var _iterator = _createForOfIteratorHelper(this.store.extensions),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _this$store$managerSe, _extension$options$ex;

        var extension = _step.value;

        if ( // managerSettings excluded this from running
        ((_this$store$managerSe = this.store.managerSettings.exclude) === null || _this$store$managerSe === void 0 ? void 0 : _this$store$managerSe.pasteRules) || // Method doesn't exist
        !extension.createPasteRules || ( // Extension settings exclude it
        (_extension$options$ex = extension.options.exclude) === null || _extension$options$ex === void 0 ? void 0 : _extension$options$ex.pasteRules)) {
          continue;
        }

        pasteRules.push(...extension.createPasteRules());
      } // TODO rewrite so this is all one plugin

    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    this.store.addPlugins(...pasteRules);
  }

}

var _dec$4, _class$4;
var DEFAULT_PERSISTENT_SELECTION_CLASS = 'selection';
/**
 * This extension adds a decoration to the selected text and can be used to
 * preserve the marker for the selection when the editor loses focus.
 */

var PersistentSelectionExtension = (_dec$4 = /*#__PURE__*/extensionDecorator({
  defaultOptions: {
    persistentSelectionClass: DEFAULT_PERSISTENT_SELECTION_CLASS
  },
  defaultPriority: ExtensionPriority.Medium,
  staticKeys: ['persistentSelectionClass']
}), /*#__PURE__*/_dec$4(_class$4 = class PersistentSelectionExtension extends PlainExtension {
  get name() {
    return 'persistentSelection';
  }

  createPlugin() {
    return {
      props: {
        decorations: (_ref) => {
          var doc = _ref.doc,
              selection = _ref.selection;

          // Do not run the extension at all when there is no actual decoration
          // to be done.
          if (!this.options.persistentSelectionClass || selection.empty) {
            return;
          }

          var decoration;
          var decorationAttrs = {
            class: this.options.persistentSelectionClass
          };

          if (isNodeSelection(selection)) {
            decoration = Decoration.node(selection.from, selection.to, decorationAttrs);
          } else {
            decoration = Decoration.inline(selection.from, selection.to, decorationAttrs);
          }

          return DecorationSet.create(doc, [decoration]);
        }
      }
    };
  }

}) || _class$4);

var _dec$5, _class$5, _plugins, _managerPlugins, _temp$3;
var PluginsExtension = (_dec$5 = /*#__PURE__*/extensionDecorator({
  defaultPriority: ExtensionPriority.Highest
}), /*#__PURE__*/_dec$5(_class$5 = (_temp$3 = (_plugins = /*#__PURE__*/new WeakMap(), _managerPlugins = /*#__PURE__*/new WeakMap(), class PluginsExtension extends PlainExtension {
  constructor() {
    var _this;

    super(...arguments);
    _this = this;

    _plugins.set(this, {
      writable: true,
      value: []
    });

    _managerPlugins.set(this, {
      writable: true,
      value: []
    });

    _defineProperty(this, "pluginKeys", object());

    _defineProperty(this, "stateGetters", new Map());

    _defineProperty(this, "getPluginStateCreator", key => state => {
      return key.getState(state !== null && state !== void 0 ? state : this.store.getState());
    });

    _defineProperty(this, "getStateByName", identifier => {
      var stateGetter = this.stateGetters.get(identifier);
      !stateGetter ? process.env.NODE_ENV !== "production" ? invariant(false, {
        message: 'No plugin exists for the requested extension name.'
      }) : invariant(false) : void 0;
      return stateGetter();
    });

    _defineProperty(this, "addPlugins", function () {
      for (var _len = arguments.length, plugins = new Array(_len), _key = 0; _key < _len; _key++) {
        plugins[_key] = arguments[_key];
      }

      _classPrivateFieldSet(_this, _plugins, [..._classPrivateFieldGet(_this, _plugins), ...plugins]);

      _this.store.setStoreKey('plugins', _classPrivateFieldGet(_this, _plugins));
    });

    _defineProperty(this, "replacePlugin", (original, replacement) => {
      _classPrivateFieldSet(this, _plugins, _classPrivateFieldGet(this, _plugins).map(plugin => plugin === original ? replacement : plugin));

      this.store.setStoreKey('plugins', _classPrivateFieldGet(this, _plugins));
    });

    _defineProperty(this, "updateExtensionPlugins", extension => {
      this.extractExtensionPlugins(extension);
      this.store.setStoreKey('plugins', _classPrivateFieldGet(this, _plugins));
    });

    _defineProperty(this, "reconfigureStatePlugins", () => {
      !(this.store.phase >= ManagerPhase.EditorView) ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.MANAGER_PHASE_ERROR,
        message: '`reconfigureStatePlugins` should only be called after the view has been added to the manager.'
      }) : invariant(false) : void 0;
      var _this$store = this.store,
          view = _this$store.view,
          updateState = _this$store.updateState;
      var newState = view.state.reconfigure({
        plugins: _classPrivateFieldGet(this, _plugins)
      });
      updateState(newState);
    });
  }

  get name() {
    return 'plugins';
  }
  /**
   * All plugins created by other extension as well.
   */


  /**
   * This extension is responsible for adding state to the editor.
   */
  onCreate() {
    var _this$store2 = this.store,
        setStoreKey = _this$store2.setStoreKey,
        setExtensionStore = _this$store2.setExtensionStore;
    this.updateExtensionStore(); // Retrieve the plugins passed in when creating the manager.

    var _this$store$managerSe = this.store.managerSettings.plugins,
        plugins = _this$store$managerSe === void 0 ? [] : _this$store$managerSe; // Add the plugins which were added directly to the manager.

    this.addOrReplacePlugins(plugins, _classPrivateFieldGet(this, _managerPlugins));

    var _iterator = _createForOfIteratorHelper(this.store.extensions),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _extension = _step.value;
        this.extractExtensionPlugins(_extension);
      } // Store the added plugins for future usage.

    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    _classPrivateFieldSet(this, _managerPlugins, plugins); // Here set the plugins keys and state getters for retrieving plugin state.
    // These methods are later used.


    setStoreKey('pluginKeys', this.pluginKeys);
    setStoreKey('getPluginState', this.getStateByName);
    setExtensionStore('getPluginState', this.getStateByName);
  }
  /**
   * Get all the plugins from the extension.
   */


  extractExtensionPlugins(extension) {
    var _this$store$managerSe2, _extension$options$ex;

    var isNotPluginCreator = !extension.createPlugin && !extension.createExternalPlugins;

    if (isNotPluginCreator || ( // the manager settings don't exclude plugins
    (_this$store$managerSe2 = this.store.managerSettings.exclude) === null || _this$store$managerSe2 === void 0 ? void 0 : _this$store$managerSe2.plugins) || ( // The extension settings don't exclude plugins
    (_extension$options$ex = extension.options.exclude) === null || _extension$options$ex === void 0 ? void 0 : _extension$options$ex.plugins)) {
      return;
    } // Create the custom plugin if it exists.


    if (extension.createPlugin) {
      var key = new PluginKey(extension.name); // Assign the plugin key to the extension name.

      this.pluginKeys[extension.name] = key;
      var getter = this.getPluginStateCreator(key);
      extension.pluginKey = key;
      extension.constructor.prototype.getPluginState = getter;
      this.stateGetters.set(extension.name, getter);
      this.stateGetters.set(extension.constructor, getter);

      var pluginSpec = _objectSpread(_objectSpread({}, extension.createPlugin()), {}, {
        key
      });

      var plugin = new Plugin(pluginSpec);
      this.addOrReplacePlugins([plugin], extension.plugin ? [extension.plugin] : undefined);
      extension.plugin = plugin;
    }

    if (extension.createExternalPlugins) {
      var externalPlugins = extension.createExternalPlugins();
      this.addOrReplacePlugins(externalPlugins, extension.externalPlugins);
      extension.externalPlugins = externalPlugins;
    }
  }

  /**
   * Add or replace a plugin.
   */
  addOrReplacePlugins(plugins, previous) {
    // This is the first time plugins are being added.
    if (!previous || isEmptyArray(previous)) {
      _classPrivateFieldSet(this, _plugins, [..._classPrivateFieldGet(this, _plugins), ...plugins]);

      return;
    } // The number of plugins and previous plugins is different.


    if (plugins.length !== previous.length) {
      // Remove previous plugins and add the new plugins to the end.
      _classPrivateFieldSet(this, _plugins, [..._classPrivateFieldGet(this, _plugins).filter(plugin => !previous.includes(plugin)), ...plugins]);

      return;
    } // The length of plugins is identical, therefore a replacement is possible.


    var pluginMap = new Map();

    for (var _i = 0, _Object$entries = Object.entries(plugins); _i < _Object$entries.length; _i++) {
      var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
          index = _Object$entries$_i[0],
          plugin = _Object$entries$_i[1];

      pluginMap.set(previous[Number.parseInt(index, 10)], plugin);
    }

    _classPrivateFieldSet(this, _plugins, _classPrivateFieldGet(this, _plugins).map(plugin => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return previous.includes(plugin) ? pluginMap.get(plugin) : plugin;
    }));
  } // Method for retrieving the plugin state by the extension name.


  /**
   * Add the plugin specific properties and methods to the manager and extension
   * store.
   */
  updateExtensionStore() {
    var _this$store3 = this.store,
        setStoreKey = _this$store3.setStoreKey,
        setExtensionStore = _this$store3.setExtensionStore; // Set the list of plugins on the manager store.

    setStoreKey('plugins', []); // Allow adding, replacing and removing plugins by other extensions.

    setExtensionStore('addPlugins', this.addPlugins);
    setExtensionStore('replacePlugin', this.replacePlugin);
    setExtensionStore('addOrReplacePlugins', this.addOrReplacePlugins);
    setExtensionStore('updateExtensionPlugins', this.updateExtensionPlugins);
    setExtensionStore('reconfigureStatePlugins', this.reconfigureStatePlugins);
  }
  /**
   * Adds a plugin to the list of plugins.
   */


}), _temp$3)) || _class$5);

var _dec$6, _class$6, _dynamicAttributes, _temp$4;
var SchemaExtension = (_dec$6 = /*#__PURE__*/extensionDecorator({
  defaultPriority: ExtensionPriority.Highest
}), /*#__PURE__*/_dec$6(_class$6 = (_temp$4 = (_dynamicAttributes = /*#__PURE__*/new WeakMap(), class SchemaExtension extends PlainExtension {
  constructor() {
    super(...arguments);

    _dynamicAttributes.set(this, {
      writable: true,
      value: {
        marks: object(),
        nodes: object()
      }
    });
  }

  get name() {
    return 'schema';
  }
  /**
   * The dynamic attributes for each node and mark extension.
   *
   * The structure will look like the following.
   *
   * ```ts
   * {
   *   paragraph: { id: () => uid(), hash: (node) => hash(node) },
   *   bold: { random: () => Math.random(), created: () => Date.now() },
   * };
   * ```
   *
   * This object is used by the created plugin to listen for changes to the doc,
   * and check for new nodes and marks which haven't yet applied the dynamic
   * attribute and add the attribute.
   */


  /**
   * This method is responsible for creating, configuring and adding the
   * `schema` to the editor. `Schema` is a special type in ProseMirror editors
   * and with `remirror` it's all just handled for you.
   */
  onCreate() {
    var _this = this;

    var managerSettings = this.store.managerSettings; // The user can override the whole schema creation process by providing
    // their own version. In that case we can exit early.

    if (managerSettings.schema) {
      var _getSpecFromSchema = getSpecFromSchema(managerSettings.schema),
          _nodes = _getSpecFromSchema.nodes,
          _marks = _getSpecFromSchema.marks;

      this.addSchema(managerSettings.schema, _nodes, _marks); // Exit early! 🙌

      return;
    } // This nodes object is built up for each extension and then at the end it
    // will be passed to the `Schema` constructor to create a new `schema`.


    var nodes = object(); // Similar to the `nodes` object above this is passed to the `Schema`.

    var marks = object(); // Get the named extra attributes from the manager. This allows each extra
    // attribute group added to the manager to be applied to the individual
    // extensions which specified.

    var namedExtraAttributes = getNamedSchemaAttributes({
      settings: managerSettings,
      gatheredSchemaAttributes: this.gatherExtraAttributes(this.store.extensions),
      nodeNames: this.store.nodeNames,
      markNames: this.store.markNames,
      tags: this.store.tags
    });

    var _iterator = _createForOfIteratorHelper(this.store.extensions),
        _step;

    try {
      var _loop = function _loop() {
        var extension = _step.value;
        // Pick the current attributes from the named attributes and merge them
        // with the extra attributes which were added to the extension. Extra
        // attributes added to the extension are prioritized.
        namedExtraAttributes[extension.name] = _objectSpread(_objectSpread({}, namedExtraAttributes[extension.name]), extension.options.extraAttributes); // There are several places that extra attributes can be ignored. This
        // checks them all.

        var ignoreExtraAttributes = managerSettings.disableExtraAttributes === true || extension.options.disableExtraAttributes === true || extension.constructor.disableExtraAttributes === true;

        if (isNodeExtension(extension)) {
          var _extension$tags;

          // Create the spec and gather dynamic attributes for this node
          // extension.
          var _createSpec = createSpec({
            createExtensionSpec: extra => extension.createNodeSpec(extra),
            extraAttributes: namedExtraAttributes[extension.name],
            ignoreExtraAttributes,
            name: extension.constructorName,
            tags: (_extension$tags = extension.tags) !== null && _extension$tags !== void 0 ? _extension$tags : []
          }),
              spec = _createSpec.spec,
              dynamic = _createSpec.dynamic; // Store the node spec on the extension for future reference.


          extension.spec = spec; // Add the spec to the nodes object which is used to create the schema
          // with the same name as the extension name.

          nodes[extension.name] = spec; // Keep track of the dynamic attributes. The `extension.name` is the
          // same name of the `NodeType` and is used by the plugin in this
          // extension to dynamically generate attributes for the correct nodes.

          _classPrivateFieldGet(_this, _dynamicAttributes).nodes[extension.name] = dynamic;
        } // Very similar to the previous conditional block except for marks rather
        // than nodes.


        if (isMarkExtension(extension)) {
          var _extension$tags2;

          // Create the spec and gather dynamic attributes for this mark
          // extension.
          var _createSpec2 = createSpec({
            createExtensionSpec: extra => extension.createMarkSpec(extra),
            extraAttributes: namedExtraAttributes[extension.name],
            ignoreExtraAttributes,
            name: extension.constructorName,
            tags: (_extension$tags2 = extension.tags) !== null && _extension$tags2 !== void 0 ? _extension$tags2 : []
          }),
              _spec = _createSpec2.spec,
              _dynamic = _createSpec2.dynamic; // Store the mark spec on the extension for future reference.


          extension.spec = _spec; // Add the spec to the `marks` object which is used to create the schema
          // with the same name as the extension name.

          marks[extension.name] = _spec;
          _classPrivateFieldGet(_this, _dynamicAttributes).marks[extension.name] = _dynamic;
        }
      };

      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        _loop();
      } // Create the schema from the gathered nodes and marks.

    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    var schema = new Schema({
      nodes,
      marks
    }); // Add the schema and nodes marks to the store.

    this.addSchema(schema, nodes, marks);
  }
  /**
   * This creates the plugin that is used to automatically create the dynamic
   * attributes defined in the extra attributes object.
   */


  createPlugin() {
    return {
      appendTransaction: (transactions, _, nextState) => {
        // This creates a new transaction which will be used to update the
        // attributes of any node and marks which
        var tr = nextState.tr; // The dynamic attribute updates only need to be run if the document has
        // been modified in a transaction.

        var documentHasChanged = transactions.some(tr => tr.docChanged);

        if (!documentHasChanged) {
          // The document has not been changed therefore no updates are
          // required.
          return null;
        } // The find children method could potentially be quite expensive. Before
        // committing to that level of work let's check that there user has
        // actually defined some dynamic attributes.


        if (isEmptyObject(_classPrivateFieldGet(this, _dynamicAttributes).nodes) || isEmptyObject(_classPrivateFieldGet(this, _dynamicAttributes).marks)) {
          return null;
        } // This function loops through every node in the document and add the
        // dynamic attributes when any relevant nodes have been added.


        findChildren({
          // The parent node is the entire document.
          node: tr.doc,
          // This means that all nodes will be checked since it always returns
          // true.
          predicate: () => true,
          // An action handler which is called whenever the predicate is truthy,
          // which in this case is all the time.
          action: child => {
            this.checkAndUpdateDynamicNodes(child, tr);
            this.checkAndUpdateDynamicMarks(child, tr);
          }
        }); // If the transaction has any `steps` then it has been modified and
        // should be returned i.e. appended to the additional transactions.
        // However, if there are no steps then ignore and return `null`.

        return tr.steps.length > 0 ? tr : null;
      }
    };
  }
  /**
   * Add the schema and nodes to the manager and extension store.
   */


  addSchema(schema, nodes, marks) {
    // Store the `nodes`, `marks` and `schema` on the manager store. For example
    // the `schema` can be accessed via `manager.store.schema`.
    this.store.setStoreKey('nodes', nodes);
    this.store.setStoreKey('marks', marks);
    this.store.setStoreKey('schema', schema); // Add the schema to the extension store, so that all extension from this
    // point have access to the schema via `this.store.schema`.

    this.store.setExtensionStore('schema', schema);
  }
  /**
   * Check the dynamic nodes to see if the provided node:
   *
   * - a) is dynamic and therefore can be updated.
   * - b) has just been created and does not yet have a value for the dynamic
   *   node.
   *
   * @param child - the node and its position.
   * @param tr - the mutable ProseMirror transaction which is applied to create
   * the next editor state
   */


  checkAndUpdateDynamicNodes(child, tr) {
    var node = child.node,
        pos = child.pos; // Check for matching nodes.

    var _iterator2 = _createForOfIteratorHelper(entries(_classPrivateFieldGet(this, _dynamicAttributes).nodes)),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var _step2$value = _slicedToArray(_step2.value, 2),
            _name = _step2$value[0],
            dynamic = _step2$value[1];

        if (node.type.name !== _name) {
          continue;
        }

        var _iterator3 = _createForOfIteratorHelper(entries(dynamic)),
            _step3;

        try {
          for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
            var _step3$value = _slicedToArray(_step3.value, 2),
                attributeName = _step3$value[0],
                attributeCreator = _step3$value[1];

            if (!isNullOrUndefined(node.attrs[attributeName])) {
              continue;
            } // The new attributes which will be added to the node.


            var attrs = _objectSpread(_objectSpread({}, node.attrs), {}, {
              [attributeName]: attributeCreator(node)
            }); // Apply the new dynamic attribute to the node via the transaction.


            tr.setNodeMarkup(pos, undefined, attrs);
          }
        } catch (err) {
          _iterator3.e(err);
        } finally {
          _iterator3.f();
        }
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }
  }
  /**
   * Loop through the dynamic marks to see if the provided node:
   *
   * - a) is wrapped by a matching mark.
   * - b) has just been added and doesn't yet have the dynamic attribute
   *   applied.
   *
   * @param child - the node and its position.
   * @param tr - the mutable ProseMirror transaction which is applied to create
   * the next editor state.
   */


  checkAndUpdateDynamicMarks(child, tr) {
    var _this2 = this;

    var node = child.node,
        pos = child.pos; // Check for matching marks.

    var _iterator4 = _createForOfIteratorHelper(entries(_classPrivateFieldGet(this, _dynamicAttributes).marks)),
        _step4;

    try {
      var _loop2 = function _loop2() {
        var _step4$value = _slicedToArray(_step4.value, 2),
            name = _step4$value[0],
            dynamic = _step4$value[1];

        // This is needed to create the new mark. Even though a mark may already
        // exist ProseMirror requires that a new one is created and added in
        // order. More details available
        // [here](https://discuss.prosemirror.net/t/updating-mark-attributes/776/2?u=ifi).
        var type = _this2.store.schema.marks[name]; // Get the attrs from the mark.

        var mark = node.marks.find(mark => mark.type.name === name); // If the mark doesn't exist within the set then move to the next
        // dynamically updated mark.

        if (!mark) {
          return "continue";
        } // Loop through to find if any of the required matches are missing from
        // the dynamic attribute;


        var _iterator5 = _createForOfIteratorHelper(entries(dynamic)),
            _step5;

        try {
          for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
            var _step5$value = _slicedToArray(_step5.value, 2),
                attributeName = _step5$value[0],
                attributeCreator = _step5$value[1];

            // When the attributes for this dynamic attributeName are already
            // defined we should move onto the next item;
            if (!isNullOrUndefined(mark.attrs[attributeName])) {
              continue;
            } // Use the starting position of the node to calculate the range range of
            // the current mark.


            var range = getMarkRange(tr.doc.resolve(pos), type);

            if (!range) {
              continue;
            } // The { from, to } range which will be used to update the mark id
            // attribute.


            var from = range.from,
                to = range.to; // Create the new mark with all the existing dynamic attributes applied.

            var newMark = type.create(_objectSpread(_objectSpread({}, mark.attrs), {}, {
              [attributeName]: attributeCreator(mark)
            })); // Update the value of the mark. The only way to do this right now is to
            // remove and then add it back again.

            tr.removeMark(from, to, type).addMark(from, to, newMark);
          }
        } catch (err) {
          _iterator5.e(err);
        } finally {
          _iterator5.f();
        }
      };

      for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
        var _ret = _loop2();

        if (_ret === "continue") continue;
      }
    } catch (err) {
      _iterator4.e(err);
    } finally {
      _iterator4.f();
    }
  }
  /**
   * Gather all the extra attributes that have been added by extensions.
   */


  gatherExtraAttributes(extensions) {
    var extraSchemaAttributes = [];

    var _iterator6 = _createForOfIteratorHelper(extensions),
        _step6;

    try {
      for (_iterator6.s(); !(_step6 = _iterator6.n()).done;) {
        var extension = _step6.value;

        if (!extension.createSchemaAttributes) {
          continue;
        }

        extraSchemaAttributes.push(...extension.createSchemaAttributes());
      }
    } catch (err) {
      _iterator6.e(err);
    } finally {
      _iterator6.f();
    }

    return extraSchemaAttributes;
  }

}), _temp$4)) || _class$6);
/**
 * With tags, you can select a specific sub selection of marks and nodes. This
 * will be the basis for adding advanced formatting to remirror.
 *
 * ```ts
 * import { ExtensionTag } from 'remirror/core';
 * import { createCoreManager, CorePreset } from 'remirror/preset/core';
 * import { WysiwygPreset } from 'remirror/preset/wysiwyg';
 *
 * const manager = createCoreManager(() => [new WysiwygPreset(), new CorePreset()], {
 *   extraAttributes: [
 *     {
 *       identifiers: {
 *         tags: [ExtensionTag.NodeBlock],
 *         type: 'node',
 *       },
 *       attributes: { role: 'presentation' },
 *     },
 *   ],
 * });
 * ```
 *
 * Each item in the tags array should be read as an `OR` so the following would
 * match `Tag1` OR `Tag2` OR `Tag3`.
 *
 * ```json
 * { tags: ["Tag1", "Tag2", "Tag3"] }
 * ```
 *
 * The `type` property (`mark | node`) is exclusive and limits the type of
 * matches that will be matched.
 */

/**
 * Get the extension extra attributes created via the manager and convert into a
 * named object which can be added to each node and mark spec.
 */
function getNamedSchemaAttributes(parameter) {
  var _settings$extraAttrib;

  var settings = parameter.settings,
      gatheredSchemaAttributes = parameter.gatheredSchemaAttributes,
      nodeNames = parameter.nodeNames,
      markNames = parameter.markNames,
      tags = parameter.tags;
  var extraAttributes = object();

  if (settings.disableExtraAttributes) {
    return extraAttributes;
  }

  var extraSchemaAttributes = [...gatheredSchemaAttributes, ...((_settings$extraAttrib = settings.extraAttributes) !== null && _settings$extraAttrib !== void 0 ? _settings$extraAttrib : [])];

  for (var _i = 0, _arr = extraSchemaAttributes !== null && extraSchemaAttributes !== void 0 ? extraSchemaAttributes : []; _i < _arr.length; _i++) {
    var attributeGroup = _arr[_i];
    var identifiers = getIdentifiers({
      identifiers: attributeGroup.identifiers,
      nodeNames,
      markNames,
      tags
    });

    var _iterator7 = _createForOfIteratorHelper(identifiers),
        _step7;

    try {
      for (_iterator7.s(); !(_step7 = _iterator7.n()).done;) {
        var _extraAttributes$iden;

        var identifier = _step7.value;
        var currentValue = (_extraAttributes$iden = extraAttributes[identifier]) !== null && _extraAttributes$iden !== void 0 ? _extraAttributes$iden : {};
        extraAttributes[identifier] = _objectSpread(_objectSpread({}, currentValue), attributeGroup.attributes);
      }
    } catch (err) {
      _iterator7.e(err);
    } finally {
      _iterator7.f();
    }
  }

  return extraAttributes;
}

/**
 * A predicate for checking if the passed in value is an `IdentifiersObject`.
 */
function isIdentifiersObject(value) {
  return isPlainObject(value) && isArray(value.tags);
}
/**
 * Get the array of names from the identifier that the extra attributes should
 * be applied to.
 */


function getIdentifiers(parameter) {
  var identifiers = parameter.identifiers,
      nodeNames = parameter.nodeNames,
      markNames = parameter.markNames,
      tags = parameter.tags;

  if (identifiers === 'nodes') {
    return nodeNames;
  }

  if (identifiers === 'marks') {
    return markNames;
  }

  if (identifiers === 'all') {
    return [...nodeNames, ...markNames];
  } // This is already an array of names to apply the attributes to.


  if (isArray(identifiers)) {
    return identifiers;
  } // Make sure the object provides is valid.


  !isIdentifiersObject(identifiers) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.EXTENSION_EXTRA_ATTRIBUTES,
    message: "Invalid value passed as an identifier when creating `extraAttributes`."
  }) : invariant(false) : void 0;
  var names = new Set();

  var _iterator8 = _createForOfIteratorHelper(identifiers.tags),
      _step8;

  try {
    for (_iterator8.s(); !(_step8 = _iterator8.n()).done;) {
      var tag = _step8.value;
      tags[tag].forEach(name => names.add(name));
    }
  } catch (err) {
    _iterator8.e(err);
  } finally {
    _iterator8.f();
  }

  var acceptableNames = identifiers.type === 'mark' ? markNames : identifiers.type === 'node' ? nodeNames : [...markNames, ...nodeNames]; // Need to filter since the tags can also be added to the `PlainExtension`.

  return [...names].filter(name => acceptableNames.includes(name));
}

/**
 * Create the scheme spec for a node or mark extension.
 *
 * @typeParam Type - either a [[Mark]] or a [[ProsemirrorNode]]
 * @param parameter - the options object [[CreateSpecParameter]]
 */
function createSpec(parameter) {
  var _spec$group$split, _spec$group;

  var createExtensionSpec = parameter.createExtensionSpec,
      extraAttributes = parameter.extraAttributes,
      ignoreExtraAttributes = parameter.ignoreExtraAttributes,
      name = parameter.name,
      tags = parameter.tags; // Keep track of the dynamic attributes which are a part of this spec.

  var dynamic = object();
  /** Called for every dynamic creator to track the dynamic attributes */

  function addDynamic(attributeName, creator) {
    dynamic[attributeName] = creator;
  } // Used to track whether the method has been called. If not called when the
  // extension spec is being set up then an error is thrown.


  var defaultsCalled = false;
  /** Called by createDefaults to track when the `defaults` has been called. */

  function onDefaultsCalled() {
    defaultsCalled = true;
  }

  var defaults = createDefaults(extraAttributes, ignoreExtraAttributes, onDefaultsCalled, addDynamic);
  var parse = createParseDOM(extraAttributes, ignoreExtraAttributes);
  var dom = createToDOM(extraAttributes, ignoreExtraAttributes);
  var spec = createExtensionSpec({
    defaults,
    parse,
    dom
  });
  !(ignoreExtraAttributes || defaultsCalled) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.EXTENSION_SPEC,
    message: "When creating a node specification you must call the 'defaults', and parse, and 'dom' methods. To avoid this error you can set the static property 'disableExtraAttributes' of '".concat(name, "' to 'true'.")
  }) : invariant(false) : void 0; // Add the tags to the group of the created spec.

  spec.group = [...((_spec$group$split = (_spec$group = spec.group) === null || _spec$group === void 0 ? void 0 : _spec$group.split(' ')) !== null && _spec$group$split !== void 0 ? _spec$group$split : []), ...tags].join(' ') || undefined;
  return {
    spec,
    dynamic
  };
}
/**
 * Get the value of the extra attribute as an object.
 *
 * This is needed because the SchemaAttributes object can be configured as a
 * string or as an object.
 */


function getExtraAttributesObject(value) {
  if (isString(value) || isFunction(value)) {
    return {
      default: value
    };
  }

  !value ? process.env.NODE_ENV !== "production" ? invariant(false, {
    message: "".concat(toString(value), " is not supported"),
    code: ErrorConstant.EXTENSION_EXTRA_ATTRIBUTES
  }) : invariant(false) : void 0;
  return value;
}
/**
 * Create the `defaults()` method which is used for setting the property.
 *
 * @param extraAttributes - the extra attributes for this particular node
 * @param shouldIgnore - whether this attribute should be ignored
 * @param onCalled - the function which is called when this is run, to check
 * that it has been added to the attrs
 * @param addDynamic - A function called to add the dynamic creator and name to
 * the store
 */


function createDefaults(extraAttributes, shouldIgnore, onCalled, addDynamicCreator) {
  return () => {
    onCalled();
    var attributes = object(); // Extra attributes can be ignored by the extension, check if that's the
    // case here.

    if (shouldIgnore) {
      return attributes;
    } // Loop through the extra attributes and attach to the attributes object.


    var _iterator9 = _createForOfIteratorHelper(entries(extraAttributes)),
        _step9;

    try {
      for (_iterator9.s(); !(_step9 = _iterator9.n()).done;) {
        var _step9$value = _slicedToArray(_step9.value, 2),
            _name2 = _step9$value[0],
            config = _step9$value[1];

        // Make sure this is an object and not a string.
        var attributesObject = getExtraAttributesObject(config);
        var defaultValue = attributesObject.default; // When true this is a dynamic attribute creator.

        if (isFunction(defaultValue)) {
          // Store the name and method of the dynamic creator.
          addDynamicCreator(_name2, defaultValue); // Set the attributes for this dynamic creator to be null by default.

          defaultValue = null;
        } // When the `defaultValue` is set to `undefined`, it is set as an empty
        // object in order for ProseMirror to set it as a required attribute.


        attributes[_name2] = defaultValue === undefined ? {} : {
          default: defaultValue
        };
      }
    } catch (err) {
      _iterator9.e(err);
    } finally {
      _iterator9.f();
    }

    return attributes;
  };
}
/**
 * Create the parseDOM method to be applied to the extension `createNodeSpec`.
 */


function createParseDOM(extraAttributes, shouldIgnore) {
  return domNode => {
    var attributes = object();

    if (shouldIgnore) {
      return attributes;
    }

    var _iterator10 = _createForOfIteratorHelper(entries(extraAttributes)),
        _step10;

    try {
      for (_iterator10.s(); !(_step10 = _iterator10.n()).done;) {
        var _domNode$getAttribute2;

        var _step10$value = _slicedToArray(_step10.value, 2),
            _name3 = _step10$value[0],
            config = _step10$value[1];

        var _getExtraAttributesOb = getExtraAttributesObject(config),
            parseDOM = _getExtraAttributesOb.parseDOM,
            other = _objectWithoutProperties(_getExtraAttributesOb, ["parseDOM"]);

        if (!isElementDomNode(domNode)) {
          continue;
        }

        if (isNullOrUndefined(parseDOM)) {
          var _domNode$getAttribute;

          attributes[_name3] = (_domNode$getAttribute = domNode.getAttribute(_name3)) !== null && _domNode$getAttribute !== void 0 ? _domNode$getAttribute : other.default;
          continue;
        }

        if (isFunction(parseDOM)) {
          var _parseDOM;

          attributes[_name3] = (_parseDOM = parseDOM(domNode)) !== null && _parseDOM !== void 0 ? _parseDOM : other.default;
          continue;
        }

        attributes[_name3] = (_domNode$getAttribute2 = domNode.getAttribute(parseDOM)) !== null && _domNode$getAttribute2 !== void 0 ? _domNode$getAttribute2 : other.default;
      }
    } catch (err) {
      _iterator10.e(err);
    } finally {
      _iterator10.f();
    }

    return attributes;
  };
}
/**
 * Create the `toDOM` method to be applied to the extension `createNodeSpec`.
 */


function createToDOM(extraAttributes, shouldIgnore) {
  return item => {
    var domAttributes = object();

    if (shouldIgnore) {
      return domAttributes;
    }

    function updateDomAttributes(value, name) {
      if (isString(value)) {
        domAttributes[name] = value;
      }

      if (isArray(value)) {
        var _value$;

        domAttributes[value[0]] = (_value$ = value[1]) !== null && _value$ !== void 0 ? _value$ : item.attrs[name];
      }

      return;
    }

    var _iterator11 = _createForOfIteratorHelper(entries(extraAttributes)),
        _step11;

    try {
      for (_iterator11.s(); !(_step11 = _iterator11.n()).done;) {
        var _step11$value = _slicedToArray(_step11.value, 2),
            _name4 = _step11$value[0],
            config = _step11$value[1];

        var _getExtraAttributesOb2 = getExtraAttributesObject(config),
            toDOM = _getExtraAttributesOb2.toDOM,
            parseDOM = _getExtraAttributesOb2.parseDOM;

        if (isNullOrUndefined(toDOM)) {
          var key = isString(parseDOM) ? parseDOM : _name4;
          domAttributes[key] = item.attrs[_name4];
          continue;
        }

        if (isFunction(toDOM)) {
          updateDomAttributes(toDOM(item.attrs, getNodeMarkOptions(item)), _name4);
          continue;
        }

        updateDomAttributes(toDOM, _name4);
      }
    } catch (err) {
      _iterator11.e(err);
    } finally {
      _iterator11.f();
    }

    return domAttributes;
  };
}
/**
 * Get the options object which applies should be used to obtain the node or
 * mark type.
 */


function getNodeMarkOptions(item) {
  if (isProsemirrorNode(item)) {
    return {
      node: item
    };
  }

  if (isProsemirrorMark(item)) {
    return {
      mark: item
    };
  }

  return {};
}
/**
 * Get the mark and node specs from provided schema.
 *
 * This is used when the user provides their own custom schema.
 */


function getSpecFromSchema(schema) {
  var nodes = entries(schema.nodes).map((_ref) => {
    var _ref2 = _slicedToArray(_ref, 2),
        name = _ref2[0],
        type = _ref2[1];

    return [name, type.spec];
  }).reduce((acc, _ref3) => {
    var _ref4 = _slicedToArray(_ref3, 2),
        name = _ref4[0],
        spec = _ref4[1];

    return _objectSpread(_objectSpread({}, acc), {}, {
      [name]: spec
    });
  }, {});
  var marks = entries(schema.marks).map((_ref5) => {
    var _ref6 = _slicedToArray(_ref5, 2),
        name = _ref6[0],
        type = _ref6[1];

    return [name, type.spec];
  }).reduce((acc, _ref7) => {
    var _ref8 = _slicedToArray(_ref7, 2),
        name = _ref8[0],
        spec = _ref8[1];

    return _objectSpread(_objectSpread({}, acc), {}, {
      [name]: spec
    });
  }, {});
  return {
    nodes,
    marks
  };
}

var _dec$7, _class$7, _temp$5;
var SuggestExtension = (_dec$7 = /*#__PURE__*/extensionDecorator({
  customHandlerKeys: ['suggester']
}), /*#__PURE__*/_dec$7(_class$7 = (_temp$5 = class SuggestExtension extends PlainExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "onAddCustomHandler", (_ref) => {
      var suggester = _ref.suggester;

      if (!suggester) {
        return;
      } // Update the suggesters with the provided suggester. Returns the cleanup
      // method.


      return addSuggester(this.store.getState(), suggester);
    });
  }

  get name() {
    return 'suggest';
  }
  /**
   * Ensure that all ssr transformers are run.
   */


  onCreate() {
    var suggesters = [];
    this.store.setExtensionStore('addSuggester', suggester => addSuggester(this.store.getState(), suggester));
    this.store.setExtensionStore('removeSuggester', suggester => removeSuggester(this.store.getState(), suggester));

    var _iterator = _createForOfIteratorHelper(this.store.extensions),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _this$store$managerSe, _extension$options$ex;

        var extension = _step.value;

        if ( // Manager settings excluded this from running
        ((_this$store$managerSe = this.store.managerSettings.exclude) === null || _this$store$managerSe === void 0 ? void 0 : _this$store$managerSe.suggesters) || // Method doesn't exist
        !extension.createSuggesters || ( // Extension settings exclude it from running
        (_extension$options$ex = extension.options.exclude) === null || _extension$options$ex === void 0 ? void 0 : _extension$options$ex.suggesters)) {
          continue;
        }

        var _suggester = extension.createSuggesters();

        var suggesterList = isArray(_suggester) ? _suggester : [_suggester];
        suggesters.push(...suggesterList);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    this.store.addPlugins(suggest(...suggesters));
  }

  createHelpers() {
    return {
      /**
       * Get the suggest plugin state.
       *
       * This may be removed at a later time.
       *
       * @experimental
       */
      getSuggestState: () => this.getState(),

      /**
       * Get some helpful methods from the SuggestPluginState.
       */
      getSuggestMethods: () => {
        var _this$getState = this.getState(),
            addIgnored = _this$getState.addIgnored,
            clearIgnored = _this$getState.clearIgnored,
            removeIgnored = _this$getState.removeIgnored,
            ignoreNextExit = _this$getState.ignoreNextExit,
            setMarkRemoved = _this$getState.setMarkRemoved,
            findMatchAtPosition = _this$getState.findMatchAtPosition,
            findNextTextSelection = _this$getState.findNextTextSelection;

        return {
          addIgnored,
          clearIgnored,
          removeIgnored,
          ignoreNextExit,
          setMarkRemoved,
          findMatchAtPosition,
          findNextTextSelection
        };
      },

      /**
       * Check to see whether the provided name is the currently active
       * suggester.
       *
       * @param name - the name of the suggester to include
       */
      isSuggesterActive: name => {
        var _this$getState$match;

        return includes(isArray(name) ? name : [name], (_this$getState$match = this.getState().match) === null || _this$getState$match === void 0 ? void 0 : _this$getState$match.suggester.name);
      }
    };
  }

  getState() {
    return getSuggestPluginState(this.store.getState());
  }

}, _temp$5)) || _class$7);

var _dec$8, _class$8, _allTags, _plainTags, _markTags, _nodeTags, _temp$6;
var TagsExtension = (_dec$8 = /*#__PURE__*/extensionDecorator({
  defaultPriority: ExtensionPriority.Highest
}), /*#__PURE__*/_dec$8(_class$8 = (_temp$6 = (_allTags = /*#__PURE__*/new WeakMap(), _plainTags = /*#__PURE__*/new WeakMap(), _markTags = /*#__PURE__*/new WeakMap(), _nodeTags = /*#__PURE__*/new WeakMap(), class TagsExtension extends PlainExtension {
  constructor() {
    super(...arguments);

    _allTags.set(this, {
      writable: true,
      value: object()
    });

    _plainTags.set(this, {
      writable: true,
      value: object()
    });

    _markTags.set(this, {
      writable: true,
      value: object()
    });

    _nodeTags.set(this, {
      writable: true,
      value: object()
    });
  }

  get name() {
    return 'tags';
  }
  /**
   * Track the tags which have been applied to the extensions in this editor.
   */


  /**
   * Create the tags which are used to identify extension with particular
   * behavioral traits.
   */
  onCreate() {
    this.resetTags();

    var _iterator = _createForOfIteratorHelper(this.store.extensions),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var extension = _step.value;
        this.updateTagForExtension(extension);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    this.store.setStoreKey('tags', _classPrivateFieldGet(this, _allTags));
    this.store.setExtensionStore('tags', _classPrivateFieldGet(this, _allTags));
    this.store.setStoreKey('plainTags', _classPrivateFieldGet(this, _plainTags));
    this.store.setExtensionStore('plainTags', _classPrivateFieldGet(this, _plainTags));
    this.store.setStoreKey('markTags', _classPrivateFieldGet(this, _markTags));
    this.store.setExtensionStore('markTags', _classPrivateFieldGet(this, _markTags));
    this.store.setStoreKey('nodeTags', _classPrivateFieldGet(this, _nodeTags));
    this.store.setExtensionStore('nodeTags', _classPrivateFieldGet(this, _nodeTags));
  }
  /**
   * Reset the tags to the empty object with empty arrays.
   */


  resetTags() {
    var allTags = object();
    var plainTags = object();
    var markTags = object();
    var nodeTags = object();

    var _iterator2 = _createForOfIteratorHelper(values(ExtensionTag)),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var tagName = _step2.value;
        allTags[tagName] = [];
        plainTags[tagName] = [];
        markTags[tagName] = [];
        nodeTags[tagName] = [];
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }

    _classPrivateFieldSet(this, _allTags, allTags);

    _classPrivateFieldSet(this, _plainTags, plainTags);

    _classPrivateFieldSet(this, _markTags, markTags);

    _classPrivateFieldSet(this, _nodeTags, nodeTags);
  }
  /**
   * Update the tags object for each extension.
   */


  updateTagForExtension(extension) {
    var _this$store$managerSe, _extension$options$ex, _extension$options$ex2, _this$store$managerSe2, _this$store$managerSe3;

    if (!extension.tags || ((_this$store$managerSe = this.store.managerSettings.exclude) === null || _this$store$managerSe === void 0 ? void 0 : _this$store$managerSe.tags) || ((_extension$options$ex = extension.options.exclude) === null || _extension$options$ex === void 0 ? void 0 : _extension$options$ex.tags)) {
      // Exit early when the editor configuration requires it.
      return;
    }

    var allTags = [...extension.tags, ...((_extension$options$ex2 = extension.options.extraTags) !== null && _extension$options$ex2 !== void 0 ? _extension$options$ex2 : []), ...((_this$store$managerSe2 = (_this$store$managerSe3 = this.store.managerSettings.extraTags) === null || _this$store$managerSe3 === void 0 ? void 0 : _this$store$managerSe3[extension.name]) !== null && _this$store$managerSe2 !== void 0 ? _this$store$managerSe2 : [])];

    for (var _i = 0, _allTags2 = allTags; _i < _allTags2.length; _i++) {
      var tag = _allTags2[_i];
      !isExtensionTag(tag) ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.EXTENSION,
        message: "The tag provided by the extension: ".concat(extension.constructorName, " is not supported by the editor. To add new tags you can use the 'mutateTag' method.")
      }) : invariant(false) : void 0; // Add tags to the combined tags stored here.

      _classPrivateFieldGet(this, _allTags)[tag].push(extension.name);

      if (isPlainExtension(extension)) {
        _classPrivateFieldGet(this, _plainTags)[tag].push(extension.name);
      }

      if (isMarkExtension(extension)) {
        _classPrivateFieldGet(this, _markTags)[tag].push(extension.name);
      }

      if (isNodeExtension(extension)) {
        _classPrivateFieldGet(this, _nodeTags)[tag].push(extension.name);
      }
    }
  }

}), _temp$6)) || _class$8);
/**
 * Check if the provided string is an extension tag.
 */

function isExtensionTag(value) {
  return includes(values(ExtensionTag), value);
}
/**
 * The shape of the tag data stored by the extension manager.
 *
 * This data can be used by other extensions to dynamically determine which
 * nodes should affected by commands / plugins / keys etc...
 */

var _dec$9, _class$9, _temp$7;
var BuiltinPreset = (_dec$9 = /*#__PURE__*/presetDecorator({
  defaultOptions: {
    exitMarksOnArrowPress: KeymapExtension.defaultOptions.exitMarksOnArrowPress,
    excludeBaseKeymap: KeymapExtension.defaultOptions.excludeBaseKeymap,
    selectParentNodeOnEscape: KeymapExtension.defaultOptions.selectParentNodeOnEscape,
    undoInputRuleOnBackspace: KeymapExtension.defaultOptions.undoInputRuleOnBackspace,
    persistentSelectionClass: PersistentSelectionExtension.defaultOptions.persistentSelectionClass
  },
  staticKeys: ['persistentSelectionClass'],
  customHandlerKeys: ['keymap', 'suggester'],
  handlerKeys: ['shouldSkipInputRule'],
  handlerKeyOptions: {
    shouldSkipInputRule: {
      earlyReturnValue: true
    }
  }
}), /*#__PURE__*/_dec$9(_class$9 = (_temp$7 = class BuiltinPreset extends Preset {
  constructor() {
    super(...arguments);

    _defineProperty(this, "onAddCustomHandler", handlers => {
      var suggester = handlers.suggester,
          keymap = handlers.keymap;

      if (suggester) {
        return this.getExtension(SuggestExtension).addCustomHandler('suggester', suggester);
      }

      if (keymap) {
        return this.getExtension(KeymapExtension).addCustomHandler('keymap', keymap);
      }

      return noop;
    });
  }

  get name() {
    return 'builtin';
  }

  onSetOptions(parameter) {
    var pickChanged = parameter.pickChanged;
    var changedKeymapOptions = pickChanged(['selectParentNodeOnEscape', 'excludeBaseKeymap', 'undoInputRuleOnBackspace']);

    if (!isEmptyObject(changedKeymapOptions)) {
      this.getExtension(KeymapExtension).setOptions(changedKeymapOptions);
    }
  }

  /**
   * The order of these extension are important.
   *
   * - [[TagsExtension]] is places first because it provides tagging which is
   *   used by the schema extension.
   * - [[SchemeExtension]] goes next because it's super important to the editor
   *   functionality and needs to run before everything else which might depend
   *   on it.
   */
  createExtensions() {
    var keymapOptions = pick(this.options, ['excludeBaseKeymap', 'selectParentNodeOnEscape', 'undoInputRuleOnBackspace']);
    var persistentSelectionOptions = pick(this.options, ['persistentSelectionClass']);
    var inputRulesExtension = new InputRulesExtension();
    inputRulesExtension.addHandler('shouldSkipInputRule', this.options.shouldSkipInputRule);
    return [// The order of these extension is important.
    new TagsExtension(), new SchemaExtension(), new AttributesExtension(), new PluginsExtension(), inputRulesExtension, new PasteRulesExtension(), new NodeViewsExtension(), new SuggestExtension(), new CommandsExtension(), new HelpersExtension(), new KeymapExtension(keymapOptions), new PersistentSelectionExtension(persistentSelectionOptions)];
  }

}, _temp$7)) || _class$9);

var _uid = /*#__PURE__*/new WeakMap();

var _getProps = /*#__PURE__*/new WeakMap();

var _previousState = /*#__PURE__*/new WeakMap();

var _firstRender = /*#__PURE__*/new WeakMap();

var _events = /*#__PURE__*/new WeakMap();

var _initialEditorState = /*#__PURE__*/new WeakMap();

/**
 * This is the `Framework` class which is used to create an abstract class for
 * implementing `Remirror` into the framework of your choice.
 *
 * The best way to learn how to use it is to take a look at the [[`DomFramework`]]
 * and [[`ReactFramework`]] implementations.
 *
 * @remarks
 *
 * There are two methods and one getter property which must be implemented for this
 */
class Framework {
  /**
   * A unique ID for the editor which can also be used as a key in frameworks
   * that need it.
   */

  /**
   * A method which enables retrieving the props from the editor.
   */

  /**
   * The private reference to the previous state.
   */

  /**
   * True when this is the first render.
   */

  /**
   * The event listener which allows consumers to subscribe to the different
   * events taking place in the editor. Events currently supported are:
   *
   * - `destroy`
   * - `focus`
   * - `blur`
   * - `updated`
   */

  /**
   * The updatable view props.
   */
  get updatableViewProps() {
    return {
      attributes: () => this.getAttributes(),
      editable: () => {
        var _this$props$editable;

        return (_this$props$editable = this.props.editable) !== null && _this$props$editable !== void 0 ? _this$props$editable : true;
      },
      nodeViews: this.manager.store.nodeViews
    };
  }
  /**
   * True when this is the first render of the editor.
   */


  get firstRender() {
    return _classPrivateFieldGet(this, _firstRender);
  }
  /**
   * Store the name of the framework.
   */


  /**
   * The props passed in when creating or updating the `Framework` instance.
   */
  get props() {
    return _classPrivateFieldGet(this, _getProps).call(this);
  }
  /**
   * Returns the previous editor state. On the first render it defaults to
   * returning the current state. For the first render the previous state and
   * current state will always be equal.
   */


  get previousState() {
    var _ref, _this$previousStateOv;

    return (_ref = (_this$previousStateOv = this.previousStateOverride) !== null && _this$previousStateOv !== void 0 ? _this$previousStateOv : _classPrivateFieldGet(this, _previousState)) !== null && _ref !== void 0 ? _ref : this.initialEditorState;
  }
  /**
   * Create the editor state from a `remirror` content type. This should be
   * passed in during initialization.
   */


  /**
   * The instance of the [[`RemirrorManager`]].
   */
  get manager() {
    return this.props.manager;
  }
  /**
   * The ProseMirror [[`EditorView`]].
   */


  get view() {
    return this.manager.view;
  }
  /**
   * The document to use for rendering and outputting HTML.
   */


  get document() {
    return getDocument(this.props.forceEnvironment);
  }
  /**
   * A unique id for the editor. Can be used to differentiate between editors.
   *
   * Please note that this ID is only locally unique, it should not be used as a
   * database key.
   */


  get uid() {
    return _classPrivateFieldGet(this, _uid);
  }

  /**
   * The initial editor state from when the editor was first created.
   */
  get initialEditorState() {
    return _classPrivateFieldGet(this, _initialEditorState);
  }

  constructor(_parameter) {
    var _this = this;

    _uid.set(this, {
      writable: true,
      value: uniqueId()
    });

    _getProps.set(this, {
      writable: true,
      value: void 0
    });

    _previousState.set(this, {
      writable: true,
      value: void 0
    });

    _firstRender.set(this, {
      writable: true,
      value: true
    });

    _events.set(this, {
      writable: true,
      value: createNanoEvents()
    });

    _initialEditorState.set(this, {
      writable: true,
      value: void 0
    });

    _defineProperty(this, "getState", () => {
      var _this$view$state;

      return (_this$view$state = this.view.state) !== null && _this$view$state !== void 0 ? _this$view$state : this.initialEditorState;
    });

    _defineProperty(this, "getPreviousState", () => this.previousState);

    _defineProperty(this, "dispatchTransaction", tr => {
      var _this$props$onDispatc, _this$props$onDispatc2, _this$props;

      // This should never happen, but it may have slipped through in the certain places.
      !!this.manager.destroyed ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.MANAGER_PHASE_ERROR,
        message: 'A transaction was dispatched to a manager that has already been destroyed. Please check your set up, or open an issue.'
      }) : invariant(false) : void 0;
      tr = (_this$props$onDispatc = (_this$props$onDispatc2 = (_this$props = this.props).onDispatchTransaction) === null || _this$props$onDispatc2 === void 0 ? void 0 : _this$props$onDispatc2.call(_this$props, tr, this.getState())) !== null && _this$props$onDispatc !== void 0 ? _this$props$onDispatc : tr;
      var previousState = this.getState();

      var _previousState$applyT = previousState.applyTransaction(tr),
          state = _previousState$applyT.state,
          transactions = _previousState$applyT.transactions;

      _classPrivateFieldSet(this, _previousState, previousState); // Use the abstract method to update the state.


      this.updateState({
        state,
        tr,
        transactions
      }); // Update the view props when an update is requested

      var forcedUpdates = this.manager.store.getForcedUpdates(tr);

      if (!isEmptyArray(forcedUpdates)) {
        this.updateViewProps(...forcedUpdates);
      }
    });

    _defineProperty(this, "onChange", function () {
      var _this$props$onChange, _this$props2;

      var parameter = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : object();
      (_this$props$onChange = (_this$props2 = _this.props).onChange) === null || _this$props$onChange === void 0 ? void 0 : _this$props$onChange.call(_this$props2, _this.eventListenerParameter(parameter));

      if (_classPrivateFieldGet(_this, _firstRender)) {
        _classPrivateFieldSet(_this, _firstRender, false);
      }
    });

    _defineProperty(this, "onBlur", event => {
      var _this$props$onBlur, _this$props3;

      var parameter = this.eventListenerParameter();
      (_this$props$onBlur = (_this$props3 = this.props).onBlur) === null || _this$props$onBlur === void 0 ? void 0 : _this$props$onBlur.call(_this$props3, parameter, event);

      _classPrivateFieldGet(this, _events).emit('blur', parameter, event);
    });

    _defineProperty(this, "onFocus", event => {
      var _this$props$onFocus, _this$props4;

      var parameter = this.eventListenerParameter();
      (_this$props$onFocus = (_this$props4 = this.props).onFocus) === null || _this$props$onFocus === void 0 ? void 0 : _this$props$onFocus.call(_this$props4, parameter, event);

      _classPrivateFieldGet(this, _events).emit('focus', parameter, event);
    });

    _defineProperty(this, "setContent", function (content) {
      var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
          _ref2$triggerChange = _ref2.triggerChange,
          triggerChange = _ref2$triggerChange === void 0 ? false : _ref2$triggerChange;

      var state = _this.createStateFromContent(content);

      _this.updateState({
        state,
        triggerChange
      });
    });

    _defineProperty(this, "clearContent", function () {
      var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref3$triggerChange = _ref3.triggerChange,
          triggerChange = _ref3$triggerChange === void 0 ? false : _ref3$triggerChange;

      _this.setContent(_this.manager.createEmptyDoc(), {
        triggerChange
      });
    });

    _defineProperty(this, "focus", position => {
      if (position === false) {
        return;
      }

      if (this.view.hasFocus() && (position === undefined || position === true)) {
        return;
      } // By using the transaction which is shared by all the commands we can make
      // the focus method, chainable.


      var tr = this.manager.tr;
      var selection = tr.selection,
          doc = tr.doc;
      var _selection$from = selection.from,
          from = _selection$from === void 0 ? 0 : _selection$from,
          _selection$to = selection.to,
          to = _selection$to === void 0 ? from : _selection$to;

      if (position === undefined || position === true) {
        position = {
          from,
          to
        };
      }

      this.dispatchSelection(tr, getTextSelection(position, doc));
    });

    _defineProperty(this, "blur", () => {
      var dom = this.view.dom;

      if (!this.view.hasFocus()) {
        return;
      }

      if (!isElementDomNode(dom)) {
        return;
      }

      dom.blur();
    });

    _defineProperty(this, "getText", state => function () {
      var lineBreakDivider = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '\n\n';

      var _ref4 = state !== null && state !== void 0 ? state : _this.getState(),
          doc = _ref4.doc;

      return doc.textBetween(0, doc.content.size, lineBreakDivider);
    });

    _defineProperty(this, "getHTML", state => () => {
      return toHtml({
        node: (state !== null && state !== void 0 ? state : this.getState()).doc,
        schema: this.manager.store.schema,
        document: this.document
      });
    });

    _defineProperty(this, "getJSON", state => () => {
      return (state !== null && state !== void 0 ? state : this.getState()).toJSON();
    });

    _defineProperty(this, "getRemirrorJSON", state => () => {
      return (state !== null && state !== void 0 ? state : this.getState()).doc.toJSON();
    });

    var getProps = _parameter.getProps,
        createStateFromContent = _parameter.createStateFromContent,
        initialEditorState = _parameter.initialEditorState,
        element = _parameter.element;

    _classPrivateFieldSet(this, _getProps, getProps);

    this.createStateFromContent = createStateFromContent;

    _classPrivateFieldSet(this, _initialEditorState, initialEditorState); // Attach the framework instance to the manager. The manager will set up the
    // update listener and manage updates to the instance of the framework
    // automatically.


    this.manager.attachFramework(this, this.updateListener.bind(this));

    if (this.manager.view) {
      return;
    } // Create the ProsemirrorView and initialize our editor manager with it.


    var view = this.createView(initialEditorState, element);
    this.manager.addView(view);
  }
  /**
   * Setup the manager event listeners which are disposed of when the manager is
   * destroyed.
   */


  updateListener(parameter) {
    var state = parameter.state,
        tr = parameter.tr;
    return _classPrivateFieldGet(this, _events).emit('updated', this.eventListenerParameter({
      state,
      tr
    }));
  }
  /**
   * Update the constructor props passed in. Useful for frameworks like react
   * where props are constantly changing and when using hooks function closures
   * can become stale.
   *
   * You can call the update method with the new `props` to update the internal
   * state of this instance.
   */


  update(parameter) {
    var getProps = parameter.getProps,
        createStateFromContent = parameter.createStateFromContent;

    _classPrivateFieldSet(this, _getProps, getProps);

    this.createStateFromContent = createStateFromContent;
    return this;
  }
  /**
   * Retrieve the editor state.
   */


  /**
   * Update the view props.
   */
  updateViewProps() {
    for (var _len = arguments.length, keys = new Array(_len), _key = 0; _key < _len; _key++) {
      keys[_key] = arguments[_key];
    }

    var props = pick(this.updatableViewProps, keys);
    this.view.setProps(_objectSpread(_objectSpread({}, this.view.props), props));
  }
  /**
   * This sets the attributes for the ProseMirror Dom node.
   */


  getAttributes(ssr) {
    var _this$manager$store, _this$props$editable2, _this$props$label;

    var _this$props5 = this.props,
        attributes = _this$props5.attributes,
        autoFocus = _this$props5.autoFocus;
    var managerAttributes = (_this$manager$store = this.manager.store) === null || _this$manager$store === void 0 ? void 0 : _this$manager$store.attributes; // The attributes which were passed in as props.

    var propAttributes = isFunction(attributes) ? attributes(this.eventListenerParameter()) : attributes; // Whether or not the editor is focused.

    var focus = {}; // In Chrome 84 when autofocus is set to any value including `"false"` it
    // will actually trigger the autofocus. This check makes sure there is no
    // `autofocus` attribute attached unless `autoFocus` is expressly a truthy
    // value.

    if (autoFocus || isNumber(autoFocus)) {
      focus = ssr ? {
        autoFocus: true
      } : {
        autofocus: 'true'
      };
    }

    var defaultAttributes = _objectSpread(_objectSpread(_objectSpread(_objectSpread({
      role: 'textbox'
    }, focus), {}, {
      'aria-multiline': 'true'
    }, !((_this$props$editable2 = this.props.editable) !== null && _this$props$editable2 !== void 0 ? _this$props$editable2 : true) ? {
      'aria-readonly': 'true'
    } : {}), {}, {
      'aria-label': (_this$props$label = this.props.label) !== null && _this$props$label !== void 0 ? _this$props$label : ''
    }, managerAttributes), {}, {
      class: cx(ssr && 'Prosemirror', EDITOR_CLASS_NAME, managerAttributes === null || managerAttributes === void 0 ? void 0 : managerAttributes.class)
    });

    return _objectSpread(_objectSpread({}, defaultAttributes), propAttributes);
  }
  /**
   * Part of the Prosemirror API and is called whenever there is state change in
   * the editor.
   *
   * @internalremarks
   * How does it work when transactions are dispatched one after the other.
   */


  /**
   * Adds `onBlur` and `onFocus` listeners.
   *
   * When extending this class make sure to call this method once
   * `ProsemirrorView` has been added to the dom.
   */
  addFocusListeners() {
    this.view.dom.addEventListener('blur', this.onBlur);
    this.view.dom.addEventListener('focus', this.onFocus);
  }
  /**
   * Remove `onBlur` and `onFocus` listeners.
   *
   * When extending this class in your framework, make sure to call this just
   * before the view is destroyed.
   */


  removeFocusListeners() {
    this.view.dom.removeEventListener('blur', this.onBlur);
    this.view.dom.removeEventListener('focus', this.onFocus);
  }
  /**
   * Called when the component unmounts and is responsible for cleanup.
   *
   * @remarks
   *
   * - Removes listeners for the editor `blur` and `focus` events
   */


  destroy() {
    // Let it clear that this instance has been destroyed.
    _classPrivateFieldGet(this, _events).emit('destroy');

    if (this.view) {
      // Remove the focus and blur listeners.
      this.removeFocusListeners();
    }
  }
  /**
   * Use this method in the `onUpdate` event to run all change handlers.
   */


  /**
   * The params used in the event listeners and the state listener
   */
  baseListenerParameter(parameter) {
    var state = parameter.state,
        tr = parameter.tr;
    return {
      tr,
      internalUpdate: !tr,
      view: this.view,
      getHTML: this.getHTML(state),
      getJSON: this.getJSON(state),
      getRemirrorJSON: this.getRemirrorJSON(state),
      getText: this.getText(state)
    };
  }
  /**
   * Creates the parameters passed into all event listener handlers. e.g.
   * `onChange`
   */


  eventListenerParameter() {
    var parameter = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : object();
    var state = parameter.state,
        tr = parameter.tr;
    return _objectSpread(_objectSpread({
      firstRender: _classPrivateFieldGet(this, _firstRender)
    }, this.baseListenerParameter({
      tr,
      state
    })), {}, {
      state: state !== null && state !== void 0 ? state : this.getState(),
      createStateFromContent: this.createStateFromContent,
      previousState: this.previousState
    });
  }
  /**
   * Set the focus for the editor.
   */


  /**
   * Needed on iOS since `requestAnimationFrame` doesn't breaks the focus
   * implementation.
   */
  handleIosFocus() {
    if (!environment.isIos) {
      return;
    }

    var element = this.view.dom;
    element.focus();
  }
  /**
   * Remove the focus from the editor. If the editor is not focused it will do
   * nothing.
   */


  /**
   * Dispatch the text selection
   */
  dispatchSelection(tr, selection) {
    // Set the selection to the requested value
    var transaction = tr.setSelection(selection);
    this.view.dispatch(transaction);
    this.handleIosFocus(); // Wait for the next event loop to set the focus.

    requestAnimationFrame(() => {
      // Use the built in focus method to refocus the editor.
      this.view.focus(); // This has to be called again in order for Safari to scroll into view
      // after the focus. Perhaps there's a better way though or maybe place
      // behind a flag.

      this.view.dispatch(this.manager.tr);
    });
  }
  /**
   * Methods and properties which are made available to all consumers of the
   * `Framework` class.
   */


  get baseOutput() {
    return _objectSpread(_objectSpread({}, this.manager.store), {}, {
      addHandler: _classPrivateFieldGet(this, _events).on.bind(_classPrivateFieldGet(this, _events)),

      /* Properties */
      uid: _classPrivateFieldGet(this, _uid),
      view: this.view,

      /* Getter Methods */
      getState: this.getState,
      getPreviousState: this.getPreviousState,
      getExtension: this.manager.getExtension.bind(this.manager),
      getPreset: this.manager.getPreset.bind(this.manager),

      /* Setter Methods */
      clearContent: this.clearContent,
      setContent: this.setContent,

      /* Helper Methods */
      focus: this.focus,
      blur: this.blur
    });
  }
  /**
   * Every framework implementation must provide it's own custom output.
   */

  /**
   * A method to get all the content in the editor as text. Depending on the
   * content in your editor, it is not guaranteed to preserve it 100%, so it's
   * best to test that it meets your needs before consuming.
   */


}

/**
 * A value that can be used in this package to prevent a method being called by
 * external users. If not present it can throw a runtime error.
 *
 * @remarks
 *
 * For example, this is the internal value which is used to check that the
 * editor manager was not created using the `new RemirrorManager` operator. It
 * must be provided to the constructor and if it isn't there it will cause the
 * manager to throw an error.
 */
var privacySymbol = /*#__PURE__*/Symbol('privacy');

/**
 * Transforms the unsorted array of presets and extension into presets and
 * sorted extensions. Handles uniqueness of extensions and automatically throws
 * an error when required extensions are missing.
 *
 * @internalremarks Currently matching by constructor - what if different
 * versions exist in the same app
 *
 * @param unionValues - the extensions to transform as well as their priorities
 *
 * @returns the list of extension instances sorted by priority
 */
function transformCombinedUnion(unionValues, settings) {
  // The items to return.
  var presets = [];
  var presetMap = new WeakMap();
  var extensions = [];
  var extensionMap = new WeakMap(); // Used to track duplicates and the presets they've been added by.

  var duplicateMap = new WeakMap(); // The unsorted, de-duped, unrefined extensions.

  var rawExtensions = [];

  var _iterator = _createForOfIteratorHelper(unionValues),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var presetOrExtension = _step.value;

      // Update the extension list in this block
      if (isExtension(presetOrExtension)) {
        var _settings$priority;

        presetOrExtension.setPriority((_settings$priority = settings.priority) === null || _settings$priority === void 0 ? void 0 : _settings$priority[presetOrExtension.name]);
        rawExtensions.push(presetOrExtension); // Keep track of the extension which have been added multiple times by
        // separate presets. Later on, the highest priority extension will be
        // added to each preset instead of the one that they configured.

        updateExtensionDuplicates({
          duplicateMap,
          extension: presetOrExtension
        });
        continue;
      } // Update the presets list in this block


      if (isPreset(presetOrExtension)) {
        presets.push(presetOrExtension);
        presetMap.set(presetOrExtension.constructor, presetOrExtension);

        var _iterator3 = _createForOfIteratorHelper(presetOrExtension.extensions),
            _step3;

        try {
          for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
            var _settings$priority2;

            var _extension = _step3.value;

            _extension.setPriority((_settings$priority2 = settings.priority) === null || _settings$priority2 === void 0 ? void 0 : _settings$priority2[_extension.name]); // Similar to the comment in the previous block. Add the preset to the
            // map for identifying extensions which are added by multiple presets.
            // Later the highest priority extension is shared amongst all presets
            // that require it to prevent instance of the same extension which would
            // break the editor.


            updateExtensionDuplicates({
              duplicateMap,
              extension: _extension,
              preset: presetOrExtension
            });
            rawExtensions.push(_extension);
          }
        } catch (err) {
          _iterator3.e(err);
        } finally {
          _iterator3.f();
        }

        continue;
      } // TODO: Allow constructors to be auto instantiated.
      // This is only reached if the passed value is invalid.


      !false ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.INVALID_MANAGER_ARGUMENTS
      }) : invariant(false) : void 0;
    } // Sort the extensions.

  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  rawExtensions = sort(rawExtensions, (a, z) => z.priority - a.priority); // Keep track of added constructors for uniqueness.

  var found = new WeakSet();
  var names = new Set(); // Remove extension duplicates and update the preset with the non duplicated
  // value.

  var _iterator2 = _createForOfIteratorHelper(rawExtensions),
      _step2;

  try {
    var _loop = function _loop() {
      var extension = _step2.value;
      var key = extension.constructor;
      var name = extension.name;
      var duplicates = duplicateMap.get(key);
      !duplicates ? process.env.NODE_ENV !== "production" ? invariant(false, {
        message: "No entries where found for the ExtensionConstructor ".concat(extension.name),
        code: ErrorConstant.INTERNAL
      }) : invariant(false) : void 0;

      if (found.has(key) || names.has(name)) {
        return "continue";
      }

      found.add(key);
      names.add(name);
      extensions.push(extension);
      extensionMap.set(key, extension); // Replace the extensions for all presets that referenced this constructor.

      duplicates.forEach(preset => preset === null || preset === void 0 ? void 0 : preset.replaceExtension(key, extension));
    };

    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var _ret = _loop();

      if (_ret === "continue") continue;
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }

  var missing = []; // Throw if any required extensions are missing.

  for (var _i = 0, _extensions = extensions; _i < _extensions.length; _i++) {
    var extension = _extensions[_i];
    findMissingExtensions({
      extension,
      found,
      missing
    });
  }

  !isEmptyArray(missing) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.MISSING_REQUIRED_EXTENSION,
    message: missing.map((_ref) => {
      var Constructor = _ref.Constructor,
          extension = _ref.extension;
      return "The extension '".concat(extension.name, "' requires '").concat(Constructor.name, " in order to run correctly.");
    }).join('\n')
  }) : invariant(false) : void 0;
  return {
    extensions,
    extensionMap,
    presets,
    presetMap
  };
}

/**
 * Populate missing Constructors.
 *
 * If any missing extensions are identified then it is the responsibility of the
 * calling method to deal with the error. Currently the action is to `throw` an
 * error.
 */
function findMissingExtensions(parameter) {
  var _extension$requiredEx;

  var extension = parameter.extension,
      found = parameter.found,
      missing = parameter.missing;

  if (!extension.requiredExtensions) {
    return;
  }

  var _iterator4 = _createForOfIteratorHelper((_extension$requiredEx = extension.requiredExtensions) !== null && _extension$requiredEx !== void 0 ? _extension$requiredEx : []),
      _step4;

  try {
    for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
      var Constructor = _step4.value;

      if (found.has(Constructor)) {
        continue;
      }

      missing.push({
        Constructor: Constructor,
        extension
      });
    }
  } catch (err) {
    _iterator4.e(err);
  } finally {
    _iterator4.f();
  }
}

/**
 * Adds the values to the duplicate map which identifies each unique extension
 * in the manager and tracks the presets responsible for adding them. This is
 * used to make sure that only one instance of each extension is shared amongst
 * the presets which require it.
 *
 * At the moment, the highest priority extension is the one that is to all
 * presets which require it. This is done by checking the `duplicateMap` for
 * each extension, and replacing the instance of the required extension within
 * the preset with the highest priority instance.
 */
function updateExtensionDuplicates(parameter) {
  var duplicateMap = parameter.duplicateMap,
      extension = parameter.extension,
      preset = parameter.preset; // The extension constructor is used as the identifier for lookups.

  var key = extension.constructor;
  var duplicate = duplicateMap.get(key);
  var presetToAdd = preset ? [preset] : [];
  duplicateMap.set(key, duplicate ? [...duplicate, ...presetToAdd] : presetToAdd);
}
/**
 * This is the object shape that is returned from the combined transformation.
 */


/**
 * This helper function extracts all the lifecycle methods from the provided
 * extension and adds them to the provided `handler` container.
 */
function extractLifecycleMethods(parameter) {
  var _extension$onCreate, _extension$onView, _extension$onStateUpd, _extension$onDestroy;

  var extension = parameter.extension,
      nodeNames = parameter.nodeNames,
      markNames = parameter.markNames,
      plainNames = parameter.plainNames,
      store = parameter.store,
      handlers = parameter.handlers; // Add the store to the extension. The store is used by extensions to access
  // all the data included in `Remirror.ExtensionStore`. I decided on this
  // pattern because passing around parameters into each call method was
  // tedious. Why not just access `this.store` within your extension to get
  // whatever you need? Also using the store allows developers to extend the
  // behaviour of their editor by adding different behaviour to the global
  // namespace [[`Remirror.ExtensionStore`]].

  extension.setStore(store); // Gather all the handlers and add them where they exist.

  var createHandler = (_extension$onCreate = extension.onCreate) === null || _extension$onCreate === void 0 ? void 0 : _extension$onCreate.bind(extension);
  var viewHandler = (_extension$onView = extension.onView) === null || _extension$onView === void 0 ? void 0 : _extension$onView.bind(extension);
  var stateUpdateHandler = (_extension$onStateUpd = extension.onStateUpdate) === null || _extension$onStateUpd === void 0 ? void 0 : _extension$onStateUpd.bind(extension);
  var destroyHandler = (_extension$onDestroy = extension.onDestroy) === null || _extension$onDestroy === void 0 ? void 0 : _extension$onDestroy.bind(extension);

  if (createHandler) {
    handlers.create.push(createHandler);
  }

  if (viewHandler) {
    handlers.view.push(viewHandler);
  }

  if (stateUpdateHandler) {
    handlers.update.push(stateUpdateHandler);
  }

  if (destroyHandler) {
    handlers.destroy.push(destroyHandler);
  } // Keep track of the names of the different types of extension held by this
  // manager. This is already in use by the [[`TagsExtension`]].


  if (isMarkExtension(extension)) {
    markNames.push(extension.name);
  } // Don't include the `doc` as a node since it is a requirement for all editors
  // and doesn't behave in the same way as other nodes.


  if (isNodeExtension(extension) && extension.name !== 'doc') {
    nodeNames.push(extension.name);
  }

  if (isPlainExtension(extension)) {
    plainNames.push(extension.name);
  }
}

/**
 * The `Manager` has multiple hook phases which are able to hook into the
 * extension manager flow and add new functionality to the editor.
 *
 * The `ExtensionEventMethod`s
 *
 * - onCreate - when the extension manager is created and after the schema is
 *   made available.
 * - onView - when the view has been received from the dom ref.
 */

/**
 * A class to manage the extensions and prosemirror interactions within the
 * editor.
 *
 * @remarks
 *
 * The RemirrorManager enables the lifecycle methods of the extensions by
 * calling each method in the distinct phases of the lifecycle.
 *
 * - `onCreate` - This happens when the manager is constructed. It calls on the
 *   extension which have an `onCreate` method and allows them to do their work.
 *
 * For the built in methods, this is when the `SchemaExtension` creates the
 * Schema and when the `TagsExtension` combines the tags for the editor
 * instance.
 *
 * ```ts
 * const manager = Manager.create([
 *   new DocExtension(),
 *   new TextExtension(),
 *   new ParagraphExtension(),
 * ])
 * ```
 *
 * At this point all the `onCreate` methods have been called. Including the
 * `onCreate` for the `Schema`.
 *
 * - `onView` - This is called the framework instance connects the
 *   `RemirrorManager` to the ProseMirror EditorView.
 *
 * ```ts
 * manager.addView(new EditorView(...))
 * manager.store.commands.insertText('Hello world');.
 * ```
 *
 * - `onStateUpdate` - This is the method called every time the ProseMirror
 *   state changes. Both the extensions and the `Framework` listen to this event
 *   and can provide updates in response.
 */

var _extensionStore$1 = /*#__PURE__*/new WeakMap();

var _extensions$1 = /*#__PURE__*/new WeakMap();

var _extensionMap$1 = /*#__PURE__*/new WeakMap();

var _presets = /*#__PURE__*/new WeakMap();

var _presetMap = /*#__PURE__*/new WeakMap();

var _store$1 = /*#__PURE__*/new WeakMap();

var _phase = /*#__PURE__*/new WeakMap();

var _settings = /*#__PURE__*/new WeakMap();

var _combined = /*#__PURE__*/new WeakMap();

var _firstStateUpdate = /*#__PURE__*/new WeakMap();

var _handlers = /*#__PURE__*/new WeakMap();

var _events$1 = /*#__PURE__*/new WeakMap();

var _framework = /*#__PURE__*/new WeakMap();

var _disposeFramework = /*#__PURE__*/new WeakMap();

class RemirrorManager {
  /**
   * The main static method for creating a manager.
   */
  static create(combined) {
    var settings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return new RemirrorManager([...getLazyArray(combined), new BuiltinPreset(settings.builtin)], _objectSpread(_objectSpread({}, settings), {}, {
      privacy: privacySymbol
    }));
  }
  /**
   * A static method to create the editor manager from an object.
   */


  static fromObject(_ref) {
    var extensions = _ref.extensions,
        presets = _ref.presets,
        _ref$settings = _ref.settings,
        settings = _ref$settings === void 0 ? {} : _ref$settings;
    return RemirrorManager.create([...extensions, ...presets], settings);
  }
  /**
   * Utility getter for storing the base method parameter which is available to
   * all extensions.
   */


  /**
   * Identifies this as a `Manager`.
   *
   * @internal
   */
  get [__INTERNAL_REMIRROR_IDENTIFIER_KEY__]() {
    return RemirrorIdentifier.Manager;
  }
  /**
   * Returns `true` if the manager has been destroyed.
   */


  get destroyed() {
    return _classPrivateFieldGet(this, _phase) === ManagerPhase.Destroy;
  }
  /**
   * `true` when the view has been added to the UI layer and the editor is
   * running.
   */


  get mounted() {
    return _classPrivateFieldGet(this, _phase) >= ManagerPhase.EditorView && _classPrivateFieldGet(this, _phase) < ManagerPhase.Destroy;
  }
  /**
   * Retrieve the framework output.
   *
   * This will throw an error if used before a framework has been attached to
   * the manager.
   *
   * With synchronous frameworks this means that it should only be used after
   * the manager has been applied to the editor creation function.
   *
   * For frameworks like react, the following example will throw an error since
   * the framework hasn't been attached yet by `RemirrorProvider`
   *
   * ```tsx
   * import React from 'react';
   * import { useManager } from 'remirror/react';
   *
   * const Editor = () => {
   *   const manager = useManager(() => [], {});
   *   log(manager.output); // ❌ Throws error
   *
   *   const callback = () => {
   *     return manager.output; // ✅ This is fine.
   *   }
   *
   *   return <RemirrorProvider manager={manager} />
   * }
   * ```
   *
   * A safe way to use this is to check if the framework is attached first.
   *
   * const Editor = () => {
   *   const manager = useManager(() => [], {});
   *
   *   if (manager.frameworkAttached) {
   *     log(manager.output); // ✅ This is fine.
   *   }
   *
   *   return <RemirrorProvider manager={manager} />
   * }
   * ```
   */


  get output() {
    var _classPrivateFieldGet2;

    !_classPrivateFieldGet(this, _framework) ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.MANAGER_PHASE_ERROR,
      message: 'Access to the manager context is only possible after the framework has been attached.'
    }) : invariant(false) : void 0;
    return (_classPrivateFieldGet2 = _classPrivateFieldGet(this, _framework)) === null || _classPrivateFieldGet2 === void 0 ? void 0 : _classPrivateFieldGet2.frameworkOutput;
  }
  /**
   * Returns true when a framework is attached to the manager.
   *
   * This can be used to check if it is safe to call `manager.output`.
   */


  get frameworkAttached() {
    return !!_classPrivateFieldGet(this, _framework);
  }
  /**
   * The extensions stored by this manager
   */


  get extensions() {
    return _classPrivateFieldGet(this, _extensions$1);
  }
  /**
   * The preset stored by this manager
   */


  get presets() {
    return _classPrivateFieldGet(this, _presets);
  }
  /**
   * Get the original combined presets used to create this manager.
   */


  get combined() {
    return freeze(_classPrivateFieldGet(this, _combined));
  }
  /**
   * Get the extension manager store which is accessible at initialization.
   */


  get store() {
    return freeze(_classPrivateFieldGet(this, _store$1));
  }
  /**
   * Provides access to the extension store.
   */


  get extensionStore() {
    return freeze(_classPrivateFieldGet(this, _extensionStore$1));
  }
  /**
   * Shorthand access to the active transaction from the manager. This is the
   * shared transaction available to all commands and should be used when you
   * need to make your commands chainable.
   *
   * If working with react and setting up your editor as a controlled component
   * then this is the preferred way to run custom commands, otherwise your
   * commands will end up being non-chainable and be overwritten by anything
   * that comes after.
   */


  get tr() {
    return this.extensionStore.getTransaction();
  }
  /**
   * Returns the stored nodes
   */


  get nodes() {
    return _classPrivateFieldGet(this, _store$1).nodes;
  }
  /**
   * Returns the store marks.
   */


  get marks() {
    return _classPrivateFieldGet(this, _store$1).marks;
  }
  /**
   * A shorthand method for retrieving the schema for this extension manager
   * from the data.
   */


  get schema() {
    return _classPrivateFieldGet(this, _store$1).schema;
  }
  /**
   * A shorthand getter for retrieving the tags from the extension manager.
   */


  get extensionTags() {
    return _classPrivateFieldGet(this, _store$1).tags;
  }
  /**
   * A shorthand way of retrieving the editor view.
   */


  get view() {
    return _classPrivateFieldGet(this, _store$1).view;
  }
  /**
   * Retrieve the settings used when creating the manager.
   */


  get settings() {
    return _classPrivateFieldGet(this, _settings);
  }
  /**
   * Creates the extension manager which is used to simplify the management of
   * the prosemirror editor.
   *
   * This should not be called directly if you want to use prioritized
   * extensions. Instead use `RemirrorManager.create([])`.
   */


  constructor(combined) {
    var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    var privacy = _ref2.privacy,
        settings = _objectWithoutProperties(_ref2, ["privacy"]);

    _extensionStore$1.set(this, {
      writable: true,
      value: void 0
    });

    _extensions$1.set(this, {
      writable: true,
      value: void 0
    });

    _extensionMap$1.set(this, {
      writable: true,
      value: void 0
    });

    _presets.set(this, {
      writable: true,
      value: void 0
    });

    _presetMap.set(this, {
      writable: true,
      value: void 0
    });

    _store$1.set(this, {
      writable: true,
      value: object()
    });

    _phase.set(this, {
      writable: true,
      value: ManagerPhase.None
    });

    _settings.set(this, {
      writable: true,
      value: void 0
    });

    _combined.set(this, {
      writable: true,
      value: void 0
    });

    _firstStateUpdate.set(this, {
      writable: true,
      value: true
    });

    _handlers.set(this, {
      writable: true,
      value: {
        create: [],
        view: [],
        update: [],
        destroy: [],
        disposers: []
      }
    });

    _events$1.set(this, {
      writable: true,
      value: createNanoEvents()
    });

    _framework.set(this, {
      writable: true,
      value: void 0
    });

    _disposeFramework.set(this, {
      writable: true,
      value: void 0
    });

    _defineProperty(this, "setStoreKey", (key, value) => {
      _classPrivateFieldGet(this, _store$1)[key] = value;
    });

    _defineProperty(this, "getStoreKey", key => {
      var value = _classPrivateFieldGet(this, _store$1)[key];

      !!isNullOrUndefined(value) ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.MANAGER_PHASE_ERROR,
        message: '`getStoreKey` should not be called before the values are available.'
      }) : invariant(false) : void 0;
      return value;
    });

    _defineProperty(this, "setExtensionStore", (key, value) => {
      !(_classPrivateFieldGet(this, _phase) <= ManagerPhase.EditorView) ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.MANAGER_PHASE_ERROR,
        message: '`setExtensionStore` should only be called during the `onCreate` lifecycle hook. Make sure to only call it within the returned methods.'
      }) : invariant(false) : void 0;
      _classPrivateFieldGet(this, _extensionStore$1)[key] = value;
    });

    _defineProperty(this, "getState", () => {
      var _classPrivateFieldGet3;

      if (_classPrivateFieldGet(this, _phase) >= ManagerPhase.EditorView) {
        return this.view.state;
      }

      !_classPrivateFieldGet(this, _framework) ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.MANAGER_PHASE_ERROR,
        message: '`getState` can only be called after the `Framework` or the `EditorView` has been added to the manager`. Check your plugins to make sure that the decorations callback uses the state argument.'
      }) : invariant(false) : void 0;
      return (_classPrivateFieldGet3 = _classPrivateFieldGet(this, _framework)) === null || _classPrivateFieldGet3 === void 0 ? void 0 : _classPrivateFieldGet3.initialEditorState;
    });

    _defineProperty(this, "updateState", state => {
      var previousState = this.getState();
      this.view.updateState(state);
      this.onStateUpdate({
        previousState,
        state
      });
    });

    _classPrivateFieldSet(this, _settings, settings);

    _classPrivateFieldSet(this, _combined, combined);

    !(privacy === privacySymbol) ? process.env.NODE_ENV !== "production" ? invariant(false, {
      message: "The extension manager can only be invoked via one of it's static methods. e.g 'RemirrorManager.create([...extensions])'.",
      code: ErrorConstant.NEW_EDITOR_MANAGER
    }) : invariant(false) : void 0;

    var _transformCombinedUni = transformCombinedUnion(combined, settings),
        extensions = _transformCombinedUni.extensions,
        extensionMap = _transformCombinedUni.extensionMap,
        presets = _transformCombinedUni.presets,
        presetMap = _transformCombinedUni.presetMap;

    _classPrivateFieldSet(this, _extensions$1, freeze(extensions));

    _classPrivateFieldSet(this, _extensionMap$1, extensionMap);

    _classPrivateFieldSet(this, _presets, freeze(presets));

    _classPrivateFieldSet(this, _presetMap, presetMap);

    _classPrivateFieldSet(this, _extensionStore$1, this.createExtensionStore());

    this.setupLifecycleHandlers();

    _classPrivateFieldSet(this, _phase, ManagerPhase.Create);

    var _iterator = _createForOfIteratorHelper(_classPrivateFieldGet(this, _handlers).create),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var handler = _step.value;
        var disposer = handler();

        _classPrivateFieldGet(this, _handlers).disposers.push(...(disposer ? [disposer] : []));
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }
  /**
   * Loops through all extensions to set up the lifecycle handlers.
   */


  setupLifecycleHandlers() {
    var store = _classPrivateFieldGet(this, _extensionStore$1);

    var handlers = _classPrivateFieldGet(this, _handlers); // Add the extension store to presets so they are able to handle it
    // properly.


    var _iterator2 = _createForOfIteratorHelper(_classPrivateFieldGet(this, _presets)),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var preset = _step2.value;
        preset.setExtensionStore(store);
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }

    var nodeNames = [];
    var markNames = [];
    var plainNames = []; // The names are stored as readonly arrays - which is the reason for not
    // just saying `store.nodeNames = []`.

    store.nodeNames = nodeNames;
    store.markNames = markNames;
    store.plainNames = plainNames;

    var _iterator3 = _createForOfIteratorHelper(_classPrivateFieldGet(this, _extensions$1)),
        _step3;

    try {
      for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
        var extension = _step3.value;
        extractLifecycleMethods({
          extension,
          nodeNames,
          markNames,
          plainNames,
          handlers,
          store
        });
      }
    } catch (err) {
      _iterator3.e(err);
    } finally {
      _iterator3.f();
    }
  }
  /**
   * Set the store key.
   */


  /**
   * Create the initial store.
   */
  createExtensionStore() {
    var store = object();
    Object.defineProperties(store, {
      extensions: {
        get: () => _classPrivateFieldGet(this, _extensions$1),
        enumerable: true
      },
      phase: {
        get: () => _classPrivateFieldGet(this, _phase),
        enumerable: true
      },
      view: {
        get: () => this.view,
        enumerable: true
      },
      managerSettings: {
        get: () => freeze(_classPrivateFieldGet(this, _settings)),
        enumerable: true
      },
      getState: {
        value: this.getState,
        enumerable: true
      },
      updateState: {
        value: this.updateState,
        enumerable: true
      },
      isMounted: {
        value: () => this.mounted,
        enumerable: true
      },
      getExtension: {
        value: this.getExtension.bind(this),
        enumerable: true
      },
      getPreset: {
        value: this.getPreset.bind(this),
        enumerable: true
      }
    });
    store.getStoreKey = this.getStoreKey;
    store.setStoreKey = this.setStoreKey;
    store.setExtensionStore = this.setExtensionStore;
    return store;
  }
  /**
   * A state getter method which is passed into the params.
   */


  /**
   * Stores the editor view on the manager
   *
   * @param view - the editor view
   */
  addView(view) {
    if (_classPrivateFieldGet(this, _phase) >= ManagerPhase.EditorView) {
      // Do nothing since a view has already been added.
      return this;
    } // invariant(this.#phase < ManagerPhase.EditorView, {code:
    //   ErrorConstant.MANAGER_PHASE_ERROR, message: 'A view has already been
    //   added to this manager. A view should only be added once.',
    // });


    _classPrivateFieldSet(this, _firstStateUpdate, true); // Update the lifecycle phase.


    _classPrivateFieldSet(this, _phase, ManagerPhase.EditorView); // Store the view.


    _classPrivateFieldGet(this, _store$1).view = view;

    var _iterator4 = _createForOfIteratorHelper(_classPrivateFieldGet(this, _handlers).view),
        _step4;

    try {
      for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
        var handler = _step4.value;
        var disposer = handler(view);

        _classPrivateFieldGet(this, _handlers).disposers.push(...(disposer ? [disposer] : []));
      }
    } catch (err) {
      _iterator4.e(err);
    } finally {
      _iterator4.f();
    }

    return this;
  }
  /**
   * Attach a framework to the manager.
   */


  attachFramework(framework, updateHandler) {
    if (_classPrivateFieldGet(this, _framework) === framework) {
      // Do nothing if the instances are identical.
      return;
    }

    if (_classPrivateFieldGet(this, _framework)) {
      var _classPrivateFieldGet4;

      // Destroy the old instance.
      _classPrivateFieldGet(this, _framework).destroy(); // Remove the event listener. This should exist.


      (_classPrivateFieldGet4 = _classPrivateFieldGet(this, _disposeFramework)) === null || _classPrivateFieldGet4 === void 0 ? void 0 : _classPrivateFieldGet4.call(this);
    } // Replace with the new instance.


    _classPrivateFieldSet(this, _framework, framework);

    _classPrivateFieldSet(this, _disposeFramework, this.addHandler('stateUpdate', updateHandler));
  }
  /* Public Methods */

  /**
   * Create an empty document for the editor based on the current schema.
   *
   * This automatically looks at the supported content for the doc and the
   * available nodes which fulfil that content in order to create a document
   * with only the minimal required content.
   *
   * This can be used in conjunction with the create state to reset the current
   * value of the editor.
   */


  createEmptyDoc() {
    var doc = this.schema.nodes.doc.createAndFill(); // Make sure the `doc` was created.

    !doc ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.INVALID_CONTENT,
      message: "An empty node could not be created due to an invalid schema."
    }) : invariant(false) : void 0;
    return doc;
  }
  /**
   * Create the editor state from content passed to this extension manager.
   */


  createState(parameter) {
    var content = parameter.content,
        document = parameter.document,
        _parameter$stringHand = parameter.stringHandler,
        stringHandler = _parameter$stringHand === void 0 ? this.settings.stringHandler : _parameter$stringHand,
        _parameter$onError = parameter.onError,
        onError = _parameter$onError === void 0 ? this.settings.onError : _parameter$onError,
        selection = parameter.selection;
    var _this$store = this.store,
        schema = _this$store.schema,
        plugins = _this$store.plugins;
    var doc = createDocumentNode({
      content,
      document,
      schema,
      stringHandler,
      onError,
      selection
    });
    var state = EditorState.create({
      schema,
      doc,
      plugins
    });

    if (!selection) {
      return state;
    }

    var tr = state.tr.setSelection(getTextSelection(selection, state.doc));
    return state.applyTransaction(tr).state;
  }
  /**
   * Add a handler to the manager.
   *
   * Currently the only event that can be listened to is the `destroy` event.
   */


  addHandler(event, cb) {
    return _classPrivateFieldGet(this, _events$1).on(event, cb);
  }
  /**
   * Update the state of the view and trigger the `onStateUpdate` lifecyle
   * method as well.
   */


  /**
   * This method should be called by the view layer every time the state is
   * updated.
   *
   * An example usage of this is within the collaboration extension.
   */
  onStateUpdate(parameter) {
    var firstUpdate = _classPrivateFieldGet(this, _firstStateUpdate);

    _classPrivateFieldGet(this, _extensionStore$1).currentState = parameter.state;
    _classPrivateFieldGet(this, _extensionStore$1).previousState = parameter.previousState;

    if (_classPrivateFieldGet(this, _firstStateUpdate)) {
      _classPrivateFieldSet(this, _phase, ManagerPhase.Runtime);

      _classPrivateFieldSet(this, _firstStateUpdate, false);
    }

    var parameterWithUpdate = _objectSpread(_objectSpread({}, parameter), {}, {
      firstUpdate
    });

    var _iterator5 = _createForOfIteratorHelper(_classPrivateFieldGet(this, _handlers).update),
        _step5;

    try {
      for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
        var handler = _step5.value;
        handler(parameterWithUpdate);
      }
    } catch (err) {
      _iterator5.e(err);
    } finally {
      _iterator5.f();
    }

    _classPrivateFieldGet(this, _events$1).emit('stateUpdate', parameterWithUpdate);
  }
  /**
   * Get the extension instance matching the provided constructor from the
   * manager.
   *
   * This will throw an error if non existent.
   */


  getExtension(Constructor) {
    var extension = _classPrivateFieldGet(this, _extensionMap$1).get(Constructor); // Throws an error if attempting to get an extension which is not present in
    // the manager.


    !extension ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.INVALID_MANAGER_EXTENSION,
      message: "'".concat(Constructor.name, "' doesn't exist within this manager. Make sure it is properly added before attempting to use it.")
    }) : invariant(false) : void 0;
    return extension;
  }
  /**
   * Get the requested preset from the manager. This will throw if the preset
   * doesn't exist within this manager.
   */


  getPreset(Constructor) {
    var preset = _classPrivateFieldGet(this, _presetMap).get(Constructor); // Throws an error if attempting to retrieve a preset which is not present
    // in the manager.


    !preset ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.INVALID_MANAGER_PRESET,
      message: "'".concat(Constructor.name, "' doesn't exist within this manager. Make sure it is properly added before attempting to use it.")
    }) : invariant(false) : void 0;
    return preset;
  }
  /**
   * Make a clone of the manager.
   *
   * @internalremarks What about the state stored in the extensions and presets,
   * does this need to be recreated as well?
   */


  clone() {
    var currentCombined = _classPrivateFieldGet(this, _combined).map(e => e.clone(e.options));

    return RemirrorManager.create(currentCombined, _classPrivateFieldGet(this, _settings));
  }
  /**
   * Recreate the manager.
   */


  recreate() {
    var combined = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    var settings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    var currentCombined = _classPrivateFieldGet(this, _combined).map(e => e.clone(e.initialOptions));

    return RemirrorManager.create([...currentCombined, ...combined], settings);
  }
  /**
   * This method should be called to destroy the manager and remove the view.
   */


  destroy() {
    var _classPrivateFieldGet5, _classPrivateFieldGet6, _this$view, _this$view$state$plug, _this$view2;

    _classPrivateFieldSet(this, _phase, ManagerPhase.Destroy);

    var _iterator6 = _createForOfIteratorHelper((_this$view$state$plug = (_this$view2 = this.view) === null || _this$view2 === void 0 ? void 0 : _this$view2.state.plugins) !== null && _this$view$state$plug !== void 0 ? _this$view$state$plug : []),
        _step6;

    try {
      for (_iterator6.s(); !(_step6 = _iterator6.n()).done;) {
        var _plugin$getState, _plugin$getState$dest;

        var plugin = _step6.value;
        (_plugin$getState = plugin.getState(this.view.state)) === null || _plugin$getState === void 0 ? void 0 : (_plugin$getState$dest = _plugin$getState.destroy) === null || _plugin$getState$dest === void 0 ? void 0 : _plugin$getState$dest.call(_plugin$getState);
      } // Make sure to destroy the framework and it's state update listener if it
      // exists.

    } catch (err) {
      _iterator6.e(err);
    } finally {
      _iterator6.f();
    }

    (_classPrivateFieldGet5 = _classPrivateFieldGet(this, _framework)) === null || _classPrivateFieldGet5 === void 0 ? void 0 : _classPrivateFieldGet5.destroy();
    (_classPrivateFieldGet6 = _classPrivateFieldGet(this, _disposeFramework)) === null || _classPrivateFieldGet6 === void 0 ? void 0 : _classPrivateFieldGet6.call(this); // Run all cleanup methods returned by the `onView` and `onCreate` methods.

    var _iterator7 = _createForOfIteratorHelper(_classPrivateFieldGet(this, _handlers).disposers),
        _step7;

    try {
      for (_iterator7.s(); !(_step7 = _iterator7.n()).done;) {
        var dispose = _step7.value;
        dispose();
      } // TODO: prevent `dispatchTransaction` from being called again

    } catch (err) {
      _iterator7.e(err);
    } finally {
      _iterator7.f();
    }

    var _iterator8 = _createForOfIteratorHelper(_classPrivateFieldGet(this, _handlers).destroy),
        _step8;

    try {
      for (_iterator8.s(); !(_step8 = _iterator8.n()).done;) {
        var onDestroy = _step8.value;
        onDestroy();
      }
    } catch (err) {
      _iterator8.e(err);
    } finally {
      _iterator8.f();
    }

    (_this$view = this.view) === null || _this$view === void 0 ? void 0 : _this$view.destroy();

    _classPrivateFieldGet(this, _events$1).emit('destroy');
  }
  /**
   * Check whether the manager includes the names or constructors provided for
   * the preset and extensions.
   *
   * Returns true if all are included, returns false otherwise.
   */


  includes(mustIncludeList) {
    // Searches can be made by either the name of the extension / preset or the
    // names of the constructor. We gather the values to check in separate
    // arrays
    var names = [];
    var extensionsAndPresets = [];

    var _iterator9 = _createForOfIteratorHelper(this.combined),
        _step9;

    try {
      for (_iterator9.s(); !(_step9 = _iterator9.n()).done;) {
        var item = _step9.value;
        names.push(item.name, item.constructorName);
        extensionsAndPresets.push(item.constructor);
      }
    } catch (err) {
      _iterator9.e(err);
    } finally {
      _iterator9.f();
    }

    return mustIncludeList.every(item => isString(item) ? includes(names, item) : includes(extensionsAndPresets, item));
  }

}

/**
 * Checks to see whether the provided value is a `RemirrorManager` instance.
 *
 * An optional parameter `mustIncludeList` is available if you want to check
 * that the manager includes all the listed extensions.
 *
 * @param value - the value to check
 * @param mustIncludeList - an array of presets and extension the manager must
 * include to pass the test. The identifier can either be the Extension / Preset
 * name e.g. `bold`, or the Extension / Preset constructor `BoldExtension`
 */
function isRemirrorManager(value, mustIncludeList) {
  if (!isRemirrorType(value) || !isIdentifierOfType(value, RemirrorIdentifier.Manager)) {
    return false;
  } // We can return true since there are no other checks to make.


  if (!mustIncludeList) {
    return true;
  }

  return value.includes(mustIncludeList);
}

var editorStyles = "remirror-editor";

export { AttributesExtension, BuiltinPreset, CommandsExtension, Framework, HelpersExtension, InputRulesExtension, KeymapExtension, MarkExtension, NodeExtension, NodeViewsExtension, PasteRulesExtension, PlainExtension, PluginsExtension, Preset, RemirrorManager, SchemaExtension, SuggestExtension, TagsExtension, delayedCommand, editorStyles, extensionDecorator, insertText, isDelayedValue, isExtension, isExtensionConstructor, isMarkExtension, isNodeExtension, isPlainExtension, isPreset, isPresetConstructor, isRemirrorManager, isValidExtensionConstructor, mutateDefaultExtensionOptions, presetDecorator, toggleMark };
